import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  AlertCircle,
  CalendarDays,
  CalendarRange,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Eraser,
  Eye,
  FileBarChart,
  Link2,
  Loader2,
  Plus,
  Sparkles,
  Trash2,
  Users,
} from 'lucide-react'
import { api, extractApiError, isPlanLimitError } from '../../lib/api'
import {
  formatDate,
  formatDateTime,
  formatTime,
  dayKey,
  formatPattern,
} from '../../lib/format'
import { useAuthStore } from '../../stores/authStore'
import { KeyMetricsPanel } from '../../components/KeyMetricsPanel'
import { StickyTimelineScrollbar } from '../../components/StickyTimelineScrollbar'
import { Breadcrumb } from '../../components/Breadcrumb'
import { DataTable } from '../../components/table/DataTable'
import { RowActionsMenu } from '../../components/table/RowActionsMenu'
import { Modal } from '../../components/ui/Modal'
import { Button } from '../../components/ui/Button'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import {
  reportKeyMetrics,
  reportRollup,
  workReport,
  workReportsResponse,
} from '../../lib/types'
import {
  useReportSchedule,
  formatTimeOnly,
  formatWeekdayShort,
  formatLocalCalendarDate,
} from '../../lib/report-schedule'
import {
  cleanContentForSubmit,
  reportContentIsEmpty,
  workRowMissingTitle,
  blockerRowMissingText,
  reportContentSig,
} from '../../lib/report-content'

/** Thin wrappers exported per route. Daily uses the new corporate form
 *  (`<DailyCorporateReportPage>`); Weekly / Monthly remain on the legacy
 *  bullet form via `<ReportSubmitPage>`. */
export function DailyReportPage() {
  return <DailyCorporateReportPage />
}
export function WeeklyReportPage() {
  return (
    <ReportSubmitPage
      type="weekly"
      icon={<CalendarDays size={24} />}
      title="Weekly Report"
      // Enable the "Draft with AI" button: the rollup endpoint reads
      // the user's daily reports for the week and pre-fills summary,
      // accomplishments, blockers, and plans. Was previously undefined
      // (i.e. hidden) because the wrapper never passed the prop.
      allowAiDraft
    />
  )
}
export function MonthlyReportPage() {
  return (
    <ReportSubmitPage
      type="monthly"
      icon={<CalendarRange size={24} />}
      title="Monthly Report"
      // Same as weekly — rolls up the month's dailies into a draft.
      allowAiDraft
    />
  )
}

/** Daily working-hours ceiling, in hours. QA ruling (2026-07-22): a
 *  realistic day incl. overtime tops out at 12h — was 24h. Caps the
 *  per-row Time input, the wheel-adjust, and the submit total; the
 *  server enforces the same limit (`reports.service.submit`). Weekly /
 *  monthly rows are NOT capped by this — a task's week can honestly
 *  exceed 12h. */
const MAX_DAILY_HOURS = 12
/** Intrinsic width of the Work Details task table. Shared by the table's
 *  `minWidth` and the floating StickyTimelineScrollbar that scrolls it, so the
 *  bar's thumb can never drift out of step with the content it drives. */
const WORK_TABLE_MIN_WIDTH = 1490

// The Time column is entered as hours + minutes but STORED as decimal hours
// (`timeSpent`), so the submit payload + the 12h cap are unchanged. Routing
// through total MINUTES makes the two representations round-trip exactly
// (e.g. 1h 20m ↔ 1.3333h) without floating-point drift.
function hoursToHM(value) {
  const totalMin = Math.round(Math.max(0, Number(value) || 0) * 60)
  return { h: Math.floor(totalMin / 60), m: totalMin % 60 }
}
function hmToHours(h, m, maxHours = MAX_DAILY_HOURS) {
  const hours = (Math.max(0, Number(h) || 0) * 60 + Math.max(0, Number(m) || 0)) / 60
  return Math.min(maxHours, hours)
}
/** "Xh Ym" from decimal hours — for the running total + summaries. */
function formatHM(value) {
  const { h, m } = hoursToHM(value)
  return `${h}h ${m}m`
}
// Meeting duration is stored in MINUTES — split/recombine for its h+m inputs.
function minutesToHM(value) {
  const totalMin = Math.max(0, Math.round(Number(value) || 0))
  return { h: Math.floor(totalMin / 60), m: totalMin % 60 }
}
function hmToMinutes(h, m, maxMin = 24 * 60) {
  return Math.min(maxMin, Math.max(0, Math.floor(Number(h) || 0) * 60 + Math.floor(Number(m) || 0)))
}

// ---------- Period helpers (mirror server) ----------

function todayIso() {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}
function isoFromDate(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}
/** Format a resolved period as a label. Whole calendar months render
 *  as "May 2026"; weeks and any custom range render as an explicit
 *  "DD MMM → DD MMM YYYY". Driven entirely by the resolved start/end so
 *  the page title and the Reporting Period box always agree. */
function fmtPeriodRange(type, startIso, endIso) {
  if (!startIso || !endIso) return ''
  const start = new Date(startIso)
  const end = new Date(endIso)
  if (type === 'monthly') {
    const lastOfMonth = new Date(
      end.getFullYear(),
      end.getMonth() + 1,
      0,
    ).getDate()
    const isWholeMonth =
      start.getDate() === 1 &&
      start.getFullYear() === end.getFullYear() &&
      start.getMonth() === end.getMonth() &&
      end.getDate() === lastOfMonth
    if (isWholeMonth) {
      return formatPattern(startIso, 'MMMM yyyy')
    }
  }
  return `${formatPattern(startIso, 'dd MMM')} → ${formatPattern(endIso, 'dd MMM yyyy')}`
}

/** The schedule-derived DEFAULT window for a fresh weekly/monthly
 *  report. Weekly = the current working week (the daily active days,
 *  e.g. Mon–Fri); monthly = the whole calendar month chosen by
 *  `monthlyScope` (previous = the just-closed month, current = the
 *  month in progress). The user can override either end on the page. */
function defaultPeriod(type, schedule) {
  const today = new Date()
  if (type === 'weekly') {
    const { start, end } = schedule.weekBoundsFor(today)
    return { start: isoFromDate(start), end: isoFromDate(end) }
  }
  const base =
    schedule.monthlyScope === 'current'
      ? today
      : new Date(today.getFullYear(), today.getMonth() - 1, 1)
  const { start, end } = schedule.monthBoundsFor(base)
  return { start: isoFromDate(start), end: isoFromDate(end) }
}

function emptyContent() {
  return {
    summary: '',
    workItems: [],
    accomplishments: [],
    blockers: [],
    plansForNext: [],
    meetings: [],
    notes: '',
    taskIds: [],
  }
}


/**
 * Build a user-facing message from a failed report submission. The
 * backend's validate.js returns a generic `message: 'Validation
 * failed.'` plus a `fields` map keyed by the failing path
 * (e.g. `content.blockers.0.blocker`). The generic message alone is
 * useless to the user, so prefer the first field-level message when
 * one is present — that's the actual reason ("Blocker description is
 * required.", "Task Name is required.", …).
 */
function reportErrorMessage(err) {
  const { message, fields } = extractApiError(err)
  if (fields && typeof fields === 'object') {
    const first = Object.values(fields).find(Boolean)
    if (first) return first
  }
  return message
}

// ---------- Submit page ----------

// ─── Local draft autosave ──────────────────────────────────────────
// The in-progress report is mirrored to localStorage (per user + type +
// exact period) so a page refresh or navigating away never loses unsaved
// edits. Restored on load, cleared on a successful submit. Best-effort —
// quota errors / private mode never block the form.
const DRAFT_PREFIX = 'taskmagix:report-draft:'
const DRAFT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000 // drop month-old drafts

function contentFromRow(row) {
  return {
    summary: row.content.summary ?? '',
    workItems: row.content.workItems ?? [],
    accomplishments: row.content.accomplishments ?? [],
    blockers: row.content.blockers ?? [],
    plansForNext: row.content.plansForNext ?? [],
    meetings: row.content.meetings ?? [],
    notes: row.content.notes ?? '',
    hoursLogged: row.content.hoursLogged,
    taskIds: row.content.taskIds ?? [],
  }
}

function readReportDraft(key) {
  if (!key) return null
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed?.content) return null
    if (parsed.savedAt && Date.now() - parsed.savedAt > DRAFT_MAX_AGE_MS) {
      localStorage.removeItem(key)
      return null
    }
    return parsed
  } catch {
    return null
  }
}

function writeReportDraft(key, content, aiDrafted) {
  if (!key) return
  try {
    localStorage.setItem(
      key,
      JSON.stringify({ content, aiDrafted, savedAt: Date.now() }),
    )
  } catch {
    /* quota / private mode — drafting is best-effort */
  }
}

function clearReportDraft(key) {
  if (!key) return
  try {
    localStorage.removeItem(key)
  } catch {
    /* ignore */
  }
}

function ReportSubmitPage({ type, icon, title, allowAiDraft }) {
  const qc = useQueryClient()
  const user = useAuthStore((s) => s.user)
  // Pull the tenant's report schedule so the period boundary (week-end
  // day-of-week, month-end day-of-month) and the submit-by deadline
  // come from one source of truth — the Company Admin's Report
  // Schedule page. Falls back to safe defaults while the fetch is in
  // flight so the page renders immediately.
  const schedule = useReportSchedule()
  // Reporting window. The default is derived from the schedule on every
  // render (weekly = working week, monthly = whole month per
  // monthlyScope) so it tracks a live admin change; `override` holds the
  // user's manual edits and is null until they touch a date, so their
  // choice wins without an effect fighting the default. The exact
  // { start, end } is what we submit and look up by, so the page and the
  // saved record can never disagree.
  const [override, setOverride] = useState(null)
  const [content, setContent] = useState(emptyContent())
  const [aiDrafted, setAiDrafted] = useState(false)
  // Deterministic period metrics from the most recent AI draft. Surfaced
  // read-only above the editable report so the user (and their manager)
  // sees hours / tasks / meetings at a glance without recomputing.
  // Cleared whenever the period changes — keyMetrics belongs to a draft,
  // not to a submitted record.
  const [keyMetrics, setKeyMetrics] = useState(null)

  // Schedule-derived default window, recomputed each render so it tracks
  // a live admin schedule change. The user's manual override (if any)
  // takes precedence.
  const autoPeriod = defaultPeriod(type, schedule)
  const periodStart = override ? override.start : autoPeriod.start
  const periodEnd = override ? override.end : autoPeriod.end

  // Local-draft plumbing (see the helpers above). `draftKey` is unique per
  // user + report type + exact period; `hydratedKeyRef` makes the load
  // effect run once per period; `baselineRef` holds the last "clean"
  // (server or empty) content so we only persist a draft when there are
  // REAL unsaved edits.
  const draftKey = user?.id
    ? `${DRAFT_PREFIX}${user.id}:${type}:${periodStart}:${periodEnd}`
    : null
  const hydratedKeyRef = useRef(null)
  const baselineRef = useRef('')

  // Upper bound for the reporting window: the END of the CURRENT week /
  // month. A report can cover the current or any past period, but never a
  // future one — there's nothing to report for a week/month that hasn't
  // happened. Enforced by the picker `max` AND the override clamp below
  // (belt-and-braces for a manually typed/pasted far-future date).
  const periodMax = isoFromDate(
    type === 'weekly'
      ? schedule.weekBoundsFor(new Date()).end
      : schedule.monthBoundsFor(new Date()).end,
  )
  const clampToMax = (value) => (value > periodMax ? periodMax : value)

  // Deadline drives the "Submit by …" chip + the overdue badge. Weekly
  // lands on the admin's Day of Week (Report Schedule) at/after the period
  // end; monthly on the period end, with the label re-stating the admin's
  // day-of-month.
  const deadline =
    type === 'weekly'
      ? schedule.weeklyDeadlineFor(periodEnd)
      : schedule.monthlyDeadlineFor(periodEnd)

  // Editing either bound snaps the WHOLE window to the calendar month
  // (monthly) or working week (weekly) that contains the picked day. A
  // monthly/weekly report always covers one whole period, so "29 Jan"
  // must map to all of January — never to a 29 Jan → 28 Feb window that
  // straddles two months. This guarantees the title and the Reporting
  // Period box always name the same month/week. Future picks are first
  // clamped to the current period (see clampToMax) before snapping.
  function snapWindow(value) {
    if (!value) return
    const anchor = new Date(`${clampToMax(value)}T00:00:00`)
    const { start, end } =
      type === 'weekly'
        ? schedule.weekBoundsFor(anchor)
        : schedule.monthBoundsFor(anchor)
    setOverride({ start: isoFromDate(start), end: isoFromDate(end) })
  }
  // Period navigation. A Weekly/Monthly report always covers ONE whole
  // period, so it's navigated a period at a time with Previous / Next
  // rather than free date entry (day-level picking only ever snapped back
  // to a whole week/month anyway). "Previous" steps into the day before
  // the current window, "Next" into the day after — snapWindow then snaps
  // each to the whole week/month containing it (and clamps Next to the
  // current period, so there's never a future window to report).
  const periodNoun = type === 'weekly' ? 'week' : 'month'
  // Move a WHOLE period at a time: anchor off periodStart and jump a full
  // week / month, then snap. NB: stepping a single day off periodEnd was
  // broken for weekly — the working week ends on Fri/Sat, and the day
  // AFTER it (Sun) still falls inside the SAME Monday-anchored week, so
  // snapWindow landed back on the same week and "Next week" did nothing.
  function shiftPeriod(dir) {
    const anchor = new Date(`${periodStart}T00:00:00`)
    if (type === 'weekly') anchor.setDate(anchor.getDate() + dir * 7)
    else anchor.setMonth(anchor.getMonth() + dir)
    snapWindow(isoFromDate(anchor))
  }
  const goPrev = () => shiftPeriod(-1)
  const goNext = () => shiftPeriod(1)
  // Already on the current period → nothing newer to move to.
  const atLatest = periodEnd >= periodMax

  // Submit-by chip copy. "Submit by 4:00 PM Fri" for weekly,
  // "Submit by 5:30 PM" for daily, "Submit by 3:00 PM on day 28" for
  // monthly. Returns null when the relevant cadence is disabled by
  // the admin — in that case the page still works, the chip just
  // doesn't render.
  const deadlineLabel = useMemo(() => {
    if (type === 'daily') {
      if (!schedule.dailyEnabled) return null
      return `Submit by ${formatTimeOnly(schedule.dailyReminderAt)}`
    }
    if (type === 'weekly') {
      if (!schedule.weeklyEnabled) return null
      return `Submit by ${formatTimeOnly(schedule.weeklyReminderAt)} ${formatWeekdayShort(deadline)}`
    }
    if (!schedule.monthlyEnabled) return null
    // Show the ACTUAL deadline date (e.g. "01/08/2026"), not "on day 1" —
    // "day 1" alone read as ambiguous (day 1 of which month?). QA 2026-08-31.
    return `Submit by ${formatTimeOnly(schedule.monthlyReminderAt)} on ${formatLocalCalendarDate(deadline)}`
  }, [type, schedule, deadline])

  // Overdue check — has the period's deadline already passed and the
  // user not submitted yet? Surfaced as a warning pill so the user
  // notices a slipping report without us blocking the submit.
  const isPastDeadline = deadline && deadline.getTime() < new Date().getTime()

  const existingQuery = useQuery({
    queryKey: ['reports', type, periodStart, periodEnd, user?.id],
    queryFn: async () => {
      const res = await api.get('/reports', {
        params: {
          type,
          from: periodStart,
          to: periodStart,
          userId: user?.id,
        },
      })
      const parsed = workReportsResponse.parse(res.data)
      // Match BOTH ends — with editable/custom ranges two reports could
      // share a start, so the period end disambiguates the exact record.
      // `?? null` because `.find` is `undefined` when no report exists yet
      // for this period, and React Query rejects an `undefined` queryFn
      // result ("Query data cannot be undefined"). The hydration effect
      // treats a falsy row as "no server report" (same as the daily query).
      return (
        parsed.data.find(
          (r) =>
            r.periodStart.slice(0, 10) === periodStart &&
            r.periodEnd.slice(0, 10) === periodEnd,
        ) ?? null
      )
    },
    enabled: Boolean(user),
  })

  // "Period at a glance" numbers, computed server-side from the period's
  // dailies by the SAME collector the AI draft uses. Fetched on page load
  // (QA 2026-07-22: the panel used to appear only after clicking "Draft
  // with AI") and recomputed when the window moves; `placeholderData`
  // keeps the previous figures on screen during the refetch instead of
  // blinking the panel out.
  const metricsQuery = useQuery({
    queryKey: ['reports', 'period-metrics', type, periodStart, periodEnd, user?.id],
    queryFn: async () => {
      const res = await api.get('/reports/period-metrics', {
        params: { type, periodStart, periodEnd },
      })
      return reportKeyMetrics.parse(res.data?.keyMetrics ?? {})
    },
    enabled: Boolean(user),
    placeholderData: (prev) => prev,
  })

  // Pre-fill once the lookup settles, with precedence:
  //   1. A locally-saved DRAFT for this exact period that differs from the
  //      server record (genuine unsaved edits left behind on refresh /
  //      navigation) — restored verbatim.
  //   2. The submitted / server record for the period.
  //   3. An empty form.
  // Gated on `hydratedKeyRef` so it runs once per period; after that the
  // autosave effect owns `content` and a late refetch can't clobber typing.
  useEffect(() => {
    if (!existingQuery.isFetched) return
    if (hydratedKeyRef.current === draftKey) return
    const row = existingQuery.data
    const base = row ? contentFromRow(row) : emptyContent()
    const baseJson = JSON.stringify(base)
    baselineRef.current = baseJson
    const draft = readReportDraft(draftKey)
    // A SUBMITTED record is locked (no re-submit), so never restore a
    // stale local draft over it — the draft could no longer be filed
    // anyway. Fall through to show the submitted content read-only.
    if (draft && !row?.submittedAt && JSON.stringify(draft.content) !== baseJson) {
      setContent({ ...emptyContent(), ...draft.content })
      setAiDrafted(Boolean(draft.aiDrafted))
      setKeyMetrics(null)
      toast.info('Restored your unsaved draft.')
    } else {
      // No genuine draft — show the server record (or empty) and drop any
      // stale draft that merely mirrors it. keyMetrics belongs to an AI
      // draft, not a submitted record, so clear it.
      clearReportDraft(draftKey)
      setContent(base)
      setAiDrafted(row ? row.aiDrafted : false)
      setKeyMetrics(null)
    }
    hydratedKeyRef.current = draftKey
  }, [existingQuery.isFetched, existingQuery.data, draftKey])

  // Mirror genuine unsaved edits to localStorage; clear the draft when the
  // form matches the clean baseline again. Runs only after hydration so the
  // initial state can never overwrite a restored draft. Also maintains
  // `isDirty` for the Clear-draft button — same predicate, one source.
  const [isDirty, setIsDirty] = useState(false)
  useEffect(() => {
    if (!draftKey || hydratedKeyRef.current !== draftKey) return
    const dirty = JSON.stringify(content) !== baselineRef.current
    setIsDirty(dirty)
    if (dirty) {
      writeReportDraft(draftKey, content, aiDrafted)
    } else {
      clearReportDraft(draftKey)
    }
  }, [content, aiDrafted, draftKey])

  const draftMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(
        '/reports/draft-rollup',
        {
          type,
          periodStart,
          periodEnd,
        },
        // The rollup calls Anthropic server-side and can run 30–90s on
        // Opus-tier models — override the 15s global axios timeout so the
        // browser doesn't abort before the AI draft returns (which would
        // surface as an error / drop to the deterministic stub).
        { timeout: 120_000 },
      )
      return reportRollup.parse(res.data)
    },
    onSuccess: (rollup) => {
      setContent({
        summary: rollup.content.summary ?? '',
        workItems: rollup.content.workItems ?? [],
        accomplishments: rollup.content.accomplishments ?? [],
        blockers: rollup.content.blockers ?? [],
        plansForNext: rollup.content.plansForNext ?? [],
        meetings: rollup.content.meetings ?? [],
        notes: rollup.content.notes ?? '',
        hoursLogged: rollup.content.hoursLogged,
        taskIds: rollup.content.taskIds ?? [],
      })
      setAiDrafted(true)
      setKeyMetrics(rollup.keyMetrics ?? null)
      // Tell the user the draft is AI-written, but never surface the raw
      // internal model id (e.g. "claude-haiku-4-5-20251001") — that's an
      // implementation detail, not something the end user should see. The
      // deterministic stub gets a generic "ready" line (no AI claim to
      // avoid saying "AI drafted" when it wasn't).
      const provider = rollup.provenance?.provider
      if (provider === 'anthropic') {
        toast.success('AI draft ready.')
      } else {
        toast.success('Draft ready — review before submitting.')
      }
    },
    onError: (err) => {
      // Plan-cap hit (monthly AI tokens): the global API interceptor already
      // shows the single amber "upgrade your plan" warning, so suppress our
      // own error toast.
      if (isPlanLimitError(err)) return
      toast.error(extractApiError(err).message)
    },
  })

  const submitMutation = useMutation({
    mutationFn: async () => {
      // Weekly + monthly send the explicit, possibly-edited window; the
      // backend stores it verbatim so the saved period matches the page.
      const body = {
        content: cleanContentForSubmit(content),
        periodStart,
        periodEnd,
      }
      const res = await api.post(`/reports/${type}`, body)
      return workReport.parse(res.data)
    },
    onSuccess: () => {
      toast.success('Report submitted.')
      // The submitted content is now the clean baseline — drop the local
      // draft so a later visit shows the submitted record, not a "restored
      // draft" of the same thing.
      baselineRef.current = JSON.stringify(content)
      clearReportDraft(draftKey)
      void qc.invalidateQueries({ queryKey: ['reports'] })
      // The Home dashboard's weekly / monthly reminder rows read from
      // this key — refresh it so a just-filed report clears its prompt
      // without relying on the socket round-trip.
      void qc.invalidateQueries({ queryKey: ['me-dashboard-overview'] })
    },
    onError: (err) => toast.error(reportErrorMessage(err)),
  })

  const isSubmitted = Boolean(existingQuery.data?.submittedAt)

  // "Clear draft" (QA 2026-07-22): after Draft with AI fills the form
  // there was no way to throw the generated content away. `isDirty` is
  // maintained by the autosave effect (it already computes exactly this
  // predicate against the clean baseline) — state, not a render-time
  // ref read, per the react-hooks rule.
  const [confirmClear, setConfirmClear] = useState(false)
  function handleClearDraft() {
    const row = existingQuery.data
    const base = row ? contentFromRow(row) : emptyContent()
    setContent(base)
    setAiDrafted(row ? row.aiDrafted : false)
    setKeyMetrics(null)
    clearReportDraft(draftKey)
    setConfirmClear(false)
    toast.success('Draft cleared.')
  }

  return (
    <div>
      <Breadcrumb current={title} homePath="/app" homeLabel="Dashboard" />

      {/* Hero — flat header consistent with Team Overview / My Tasks */}
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div className="flex items-start gap-3">
          <span className="h-12 w-12 rounded-xl bg-[#D1FAE5] text-[#059669] border border-[#A7F3D0] flex items-center justify-center shrink-0">
            {icon}
          </span>
          <div>
            <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)]">
              {title}
            </h1>
            <p className="mt-1 text-[color:var(--color-ink-muted)]">
              {fmtPeriodRange(type, periodStart, periodEnd)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isSubmitted ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold bg-[color:var(--color-success-bg)] text-[color:var(--color-success)] border border-[color:var(--color-success-border)]">
              <CheckCircle2 size={14} />
              Submitted
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)] border border-[color:var(--color-warning-border)]">
              <AlertCircle size={14} />
              Not submitted
            </span>
          )}
        </div>
      </div>

      {/* Period picker */}
      <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5 mb-5 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-3">
          <span className="h-12 w-12 rounded-xl bg-[#DBEAFE] text-[#2563EB] border border-[#BFDBFE] flex items-center justify-center shrink-0">
            {type === 'weekly' ? (
              <CalendarDays size={24} />
            ) : (
              <CalendarRange size={24} />
            )}
          </span>
          <div>
            <h3 className="text-sm uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] m-0">
              Reporting Period
            </h3>
            <p className="mt-0.5 text-base font-semibold text-[color:var(--color-ink-strong)]">
              {/* Same label as the page title — formatted from the
               *  resolved start/end so the box, the title, and the
               *  saved period are always identical. */}
              {fmtPeriodRange(type, periodStart, periodEnd)}
            </p>
            {/* Submit-by deadline + overdue badge — sourced from the
             *  Company Admin's Report Schedule so the user knows
             *  exactly when this report is due without flipping
             *  between pages. */}
            {/* Once the report is submitted, this line must show WHEN it was
             *  filed — not the stale "Submit by …" deadline (and never the
             *  Overdue badge). Before submission it shows the deadline, with
             *  the Overdue badge added once that deadline has passed. */}
            {isSubmitted ? (
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-[color:var(--color-success-bg)] text-[color:var(--color-success)] border-[color:var(--color-success-border)]">
                  <CheckCircle2 size={11} />
                  Submitted on {formatDateTime(existingQuery.data.submittedAt)}
                </span>
              </div>
            ) : deadlineLabel ? (
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <span
                  className={
                    'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ' +
                    (isPastDeadline
                      ? 'bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)] border-[color:var(--color-warning-border)]'
                      : 'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border-[color:var(--color-accent-border)]')
                  }
                >
                  {deadlineLabel}
                </span>
                {isPastDeadline ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[color:var(--color-priority-urgent-bg)] text-[color:var(--color-priority-urgent)] border border-[color:var(--color-priority-urgent-border)]">
                    Overdue
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
        <div className="ml-auto flex flex-wrap items-end gap-3">
          {/* Period navigation — move a whole week/month at a time. Next is
           *  disabled on the current period (no future period to report). */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={goPrev}
              title={`Previous ${periodNoun}`}
              className="inline-flex items-center gap-1.5 px-3 h-10 rounded-md text-sm font-medium border border-[color:var(--color-rule)] text-[color:var(--color-ink)] bg-[color:var(--color-canvas)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
            >
              <ChevronLeft size={16} />
              Previous {periodNoun}
            </button>
            <button
              type="button"
              onClick={goNext}
              disabled={atLatest}
              title={
                atLatest
                  ? `You're on the current ${periodNoun}`
                  : `Next ${periodNoun}`
              }
              className="inline-flex items-center gap-1.5 px-3 h-10 rounded-md text-sm font-medium border border-[color:var(--color-rule)] text-[color:var(--color-ink)] bg-[color:var(--color-canvas)] hover:bg-[color:var(--color-card-hover)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[color:var(--color-canvas)]"
            >
              Next {periodNoun}
              <ChevronRight size={16} />
            </button>
          </div>
          {allowAiDraft ? (
            <button
              type="button"
              onClick={() => draftMutation.mutate()}
              // Submitted reports are locked — drafting over one would
              // produce edits that can never be filed.
              disabled={draftMutation.isPending || isSubmitted}
              title={isSubmitted ? 'This report has already been submitted.' : undefined}
              className="inline-flex items-center gap-1.5 px-3 h-10 rounded-md text-sm font-medium border border-[color:var(--color-accent-border)] bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] hover:opacity-90 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {draftMutation.isPending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Sparkles size={14} />
              )}
              Draft with AI
            </button>
          ) : null}
          {/* Clear/reset for AI-drafted or typed content (QA-requested).
           *  Hidden once submitted — the form is locked then anyway. */}
          {!isSubmitted ? (
            <button
              type="button"
              onClick={() => setConfirmClear(true)}
              disabled={!isDirty || draftMutation.isPending}
              title={
                isDirty
                  ? 'Reset the form for this period'
                  : 'Nothing to clear — the form matches the last saved state'
              }
              className="inline-flex items-center gap-1.5 px-3 h-10 rounded-md text-sm font-medium border border-[color:var(--color-rule)] text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[color:var(--color-ink-muted)]"
            >
              <Eraser size={14} />
              Clear draft
            </button>
          ) : null}
        </div>
      </div>

      <ConfirmDialog
        open={confirmClear}
        onCancel={() => setConfirmClear(false)}
        onConfirm={handleClearDraft}
        title="Clear This Draft?"
        message="This removes the AI-drafted and edited content for this period and resets the form. Anything not submitted will be lost."
        confirmLabel="Clear Draft"
      />

      {/* Read-only key metrics — computed server-side from the source
       *  dailies so the numbers can never drift from what the user
       *  actually logged. Shown from PAGE LOAD via the period-metrics
       *  endpoint (QA: it used to appear only after "Draft with AI");
       *  a fresh AI draft's copy of the same figures takes precedence
       *  merely because it's newest. */}
      {(keyMetrics ?? metricsQuery.data) ? (
        <KeyMetricsPanel
          metrics={keyMetrics ?? metricsQuery.data}
          periodType={type}
        />
      ) : null}

      {existingQuery.isLoading ? (
        <p className="text-sm text-[color:var(--color-ink-muted)] inline-flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading report…
        </p>
      ) : (
        // A submitted report is locked (QA: no Re-submit once filed).
        // The native fieldset[disabled] disables every input / textarea /
        // select / button inside the form body in one place, so the
        // filed content shows read-only without threading a prop
        // through every section component.
        <fieldset
          disabled={isSubmitted}
          className="m-0 p-0 border-0 min-w-0 block"
        >
          <ReportFormBody
            content={content}
            onChange={setContent}
            aiDrafted={aiDrafted}
            periodNoun={type === 'weekly' ? 'this week' : 'this month'}
          />
        </fieldset>
      )}

      <div className="mt-6 flex items-center justify-end gap-2">
        {isSubmitted ? (
          <p className="text-sm text-[color:var(--color-ink-muted)] inline-flex items-center gap-1.5 m-0">
            <CheckCircle2
              size={14}
              className="text-[color:var(--color-success)]"
            />
            Submitted
            {existingQuery.data?.submittedAt
              ? ` on ${formatDateTime(existingQuery.data.submittedAt)}`
              : ''}{' '}
            — this report can no longer be edited.
          </p>
        ) : (
          <button
            type="button"
            onClick={() => {
              // A blank form has nothing to report — block it here so the
              // user gets an instant, clear message instead of filing an
              // empty record (the server rejects it too, defense-in-depth).
              if (reportContentIsEmpty(content)) {
                toast.error(
                  'Add at least one entry before submitting your report.',
                )
                return
              }
              // A blocker row with a status/dependency but no blocker
              // text must be described, not silently dropped on submit
              // (QA) — same rule the daily form enforces.
              if ((content.blockers ?? []).some(blockerRowMissingText)) {
                toast.error(
                  'Describe the blocker in every row that has a dependency or status set.',
                )
                return
              }
              submitMutation.mutate()
            }}
            disabled={submitMutation.isPending}
            className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full text-sm font-semibold bg-[color:var(--color-primary)] text-white shadow-sm hover:shadow hover:bg-[color:var(--color-primary-hover)] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitMutation.isPending ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <CheckCircle2 size={14} />
            )}
            {submitMutation.isPending ? 'Submitting…' : 'Submit report'}
          </button>
        )}
      </div>
    </div>
  )
}

// ---------- Key metrics panel ----------

/**
 * Read-only stat tiles rendered above the editable weekly / monthly form.
 * Numbers come straight from the rollup endpoint's `keyMetrics` payload —
 * NOT from the AI's narrative — so they can never disagree with the
 * source dailies even if Claude rephrases the summary.
 *
 * Hidden on daily reports (no rollup happens there) and on submitted
 * records loaded from the DB (keyMetrics belongs to a fresh draft, not
 * a saved record).
 */
// `KeyMetricsPanel` moved to components/KeyMetricsPanel.jsx so the Team Reports
// detail (WorkReportsPage) renders the identical "Period at a glance" cards.

// ---------- Shared form body ----------

function ReportFormBody({
  content,
  onChange,
  // "this week" / "this month" — used for period-aware empty-state copy
  // so the weekly/monthly forms don't say "today".
  periodNoun = 'today',
}) {
  function updateBullet(
    key,
    idx,
    value,
  ) {
    onChange({
      ...content,
      [key]: content[key].map((b, i) => (i === idx ? value : b)),
    })
  }
  function removeBullet(
    key,
    idx,
  ) {
    onChange({
      ...content,
      [key]: content[key].filter((_, i) => i !== idx),
    })
  }
  function addBullet(key) {
    onChange({ ...content, [key]: [...content[key], ''] })
  }
  // Blockers are a STRUCTURED row (blocker + dependencyOn +
  // resolutionStatus), rendered with the same <BlockersSection> the
  // Daily form uses. The Weekly / Monthly forms previously flattened
  // this to plain blocker text, so the Dependency + Status fields could
  // never be filled during creation even though the read-only Team
  // Reports view shows those columns — the defect a tester reported.
  function patchBlocker(idx, patch) {
    onChange({
      ...content,
      blockers: content.blockers.map((b, i) =>
        i === idx ? { ...b, ...patch } : b,
      ),
    })
  }
  function removeBlocker(idx) {
    onChange({
      ...content,
      blockers: content.blockers.filter((_, i) => i !== idx),
    })
  }
  function addBlocker() {
    onChange({
      ...content,
      blockers: [
        ...content.blockers,
        { blocker: '', dependencyOn: '', resolutionStatus: 'open' },
      ],
    })
  }

  return (
    <div className="space-y-4">
      {/* AI-drafted banner removed — AI usage is no longer surfaced
       *  on work reports. The `aiDrafted` prop still threads through
       *  for back-compat with existing rows but renders nothing. */}

      <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4">
        <label className="block text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1">
          Summary
        </label>
        {/* min/max-height caps the drag-resize so the field can't be
         *  stretched past a usable height and wreck the layout (QA). */}
        <textarea
          rows={3}
          value={content.summary ?? ''}
          onChange={(e) => onChange({ ...content, summary: e.target.value })}
          placeholder="Enter a Summary"
          className="w-full min-h-[80px] max-h-[300px] px-3 py-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] resize-y"
        />
      </div>

      <BulletSection
        title="Accomplishments"
        helper="What got done — tasks shipped, decisions made, problems solved."
        items={content.accomplishments}
        onUpdate={(i, v) => updateBullet('accomplishments', i, v)}
        onRemove={(i) => removeBullet('accomplishments', i)}
        onAdd={() => addBullet('accomplishments')}
      />
      {/* Structured Issues & Dependencies — Blocker + Dependency +
       *  Status, identical to the Daily form. Replaces the old
       *  text-only bullet list so Dependency details can be entered at
       *  creation time (they're shown as a column when the report is
       *  viewed). */}
      <BlockersSection
        items={content.blockers}
        onPatch={patchBlocker}
        onRemove={removeBlocker}
        onAdd={addBlocker}
        emptyLabel={`No blockers logged ${periodNoun}.`}
      />
      <BulletSection
        title="Plans for Next"
        helper="What's next on your plate."
        items={content.plansForNext}
        onUpdate={(i, v) => updateBullet('plansForNext', i, v)}
        onRemove={(i) => removeBullet('plansForNext', i)}
        onAdd={() => addBullet('plansForNext')}
      />

      {/* "Hours logged" form field removed 2026-05-27 along with the
       *  rest of the Time Tracking module. Existing reports keep
       *  their stored `content.hoursLogged` value in Mongo; the form
       *  just stops asking for it. If your team needs the field
       *  back, it lives at `report.content.hoursLogged` and the
       *  reports listing still surfaces it for legacy entries. */}
    </div>
  )
}

function BulletSection({
  title,
  helper,
  items,
  onUpdate,
  onRemove,
  onAdd,
}) {
  return (
    <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4 mb-4">
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="text-sm uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] m-0">
          {title}
        </h3>
        {/* Count only filled bullets — a blank placeholder row is dropped
         *  on submit (see cleanContentForSubmit), so it must not inflate
         *  the section total. */}
        <p className="text-sm text-[color:var(--color-ink-subtle)]">
          {items.filter((it) => (it ?? '').trim().length > 0).length}
        </p>
      </div>
      <p className="text-sm text-[color:var(--color-ink-muted)] mb-2">
        {helper}
      </p>
      <div className="space-y-2">
        {items.length === 0 ? (
          <p className="text-sm text-[color:var(--color-ink-muted)] italic">
            Nothing yet.
          </p>
        ) : (
          items.map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--color-accent-dim)] shrink-0" />
              <input
                value={item}
                onChange={(e) => onUpdate(i, e.target.value)}
                className="flex-1 h-9 px-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-sm text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)]"
              />
              <button
                type="button"
                onClick={() => onRemove(i)}
                aria-label="Remove"
                title="Remove"
                className="p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))
        )}
      </div>
      <div className="mt-2">
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex items-center gap-1.5 text-xs text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] cursor-pointer"
        >
          <Plus size={12} />
          Add a Bullet
        </button>
      </div>
    </div>
  )
}

// ---------- Team Reports (Manager / Team Leader) ----------
//
// Visual + structural mirror of the Company Admin Work Reports page
// (`pages/app/WorkReportsPage.jsx`) so the two surfaces read
// identically — same filter bar, same table layout, same modal-based
// detail view, same Work Items / Issues & Dependencies /
// Communication & Collaboration blocks in the modal. Only the data
// scope differs: backend serves department-scoped rows for Manager /
// Team Leader, workspace-wide rows for Company Admin (handled
// transparently by `/reports` based on the actor's operations).

/** Format a report's period — single date for daily, range for
 *  weekly/monthly. Mirrors `fmtPeriod()` in WorkReportsPage. */
function fmtTeamReportPeriod(r) {
  if (r.type === 'daily') return formatDate(r.periodStart)
  return `${formatDate(r.periodStart)} → ${formatDate(r.periodEnd)}`
}

/** Modal subtitle: reporting period + submitted timestamp. Mirrors
 *  `reportSubtitle()` in WorkReportsPage — collapses the submitted date to
 *  a time-only suffix when it lands on the same calendar day as the period
 *  (daily reports) so the date isn't printed twice; keeps the full
 *  date+time when they differ (weekly / monthly). */
function teamReportSubtitle(r) {
  const period = fmtTeamReportPeriod(r)
  if (!r.submittedAt) return period
  const sameDay = dayKey(r.submittedAt) === dayKey(r.periodStart)
  const submitted = sameDay
    ? formatTime(r.submittedAt)
    : formatDateTime(r.submittedAt)
  return `${period} · Submitted ${submitted}`
}

/** True when a report's content has anything worth rendering. Used to
 *  decide between the section blocks and the "no content" fallback in the
 *  Team Reports detail modal. */
function teamReportHasContent(c) {
  if (!c) return false
  return (
    Boolean((c.summary ?? '').trim()) ||
    Boolean((c.notes ?? '').trim()) ||
    (c.workItems?.length ?? 0) > 0 ||
    (c.accomplishments?.length ?? 0) > 0 ||
    (c.blockers?.length ?? 0) > 0 ||
    (c.plansForNext?.length ?? 0) > 0 ||
    (c.meetings?.length ?? 0) > 0
  )
}

export function TeamReportsPage() {
  // Detail view opens as a Modal — `openReport` holds the selected row's
  // full shape so the dialog can render without a second fetch.
  const [openReport, setOpenReport] = useState(null)
  // Who can see EVERY department's reports: a company-wide member
  // (reports.view_workspace) — plus Company Admin / Sub Admin for safety,
  // though they normally use the admin Work Reports page. They get a
  // Department FILTER + column. A department-scoped member
  // (reports.view_department) only ever receives their own department's
  // reports from the backend, so no Department facet is shown for them.
  const canSeeAllDepartments = useAuthStore(
    (s) =>
      s.user?.role === 'company_admin' ||
      s.user?.role === 'sub_admin' ||
      s.hasOperation('reports.view_workspace'),
  )

  // Whole-page gate. The sidebar link is hidden for an employee, but the route
  // `/app/reports/team` carries no guard, so typing the URL rendered the page
  // for anyone — a self-scope viewer got "reports submitted by your team"
  // showing only their own. The data was never exposed (the API scopes every
  // read to the actor's audience), but the page should not be reachable.
  //
  // Same predicate as `canSeeTeamReports` in components/layout/UserLayout.jsx,
  // so the nav and the page can't drift apart. `team-analytics` does the
  // equivalent off its roster 403; this list has no error to key on, because a
  // self-scope read succeeds and simply returns your own rows.
  const canSeeTeamReports = useAuthStore((s) =>
    s.hasAnyOperation([
      'pages.team_reports',
      'reports.view_department',
      'reports.view_workspace',
    ]),
  )

  // Mirrors the server gate (`reports.service.js#assertMayDeleteReports`): a
  // work-reports ADMIN, or a SUPERVISOR whose Work Role already gives them
  // reach over other people's reports. Externals never qualify.
  //
  // This only decides whether to OFFER the action — the server re-checks the
  // actor AND every selected report, so a stale token or a hand-rolled request
  // still cannot delete outside the caller's audience.
  const canDeleteReports = useAuthStore(
    (s) =>
      s.user?.membershipType !== 'external' &&
      (s.user?.role === 'company_admin' ||
        s.hasOperation('work_reports.view') ||
        s.hasOperation('reports.view_workspace') ||
        s.hasOperation('reports.view_department')),
  )

  // Pending bulk deletion: { ids, clearSelection } while the reason dialog is
  // open. A reason is mandatory — the record is a compliance artefact and the
  // API refuses a blank one, so the dialog enforces it rather than letting the
  // request bounce.
  const [bulkDelete, setBulkDelete] = useState(null)
  const [deleteReason, setDeleteReason] = useState('')

  const bulkDeleteMutation = useMutation({
    mutationFn: async ({ ids, reason }) =>
      (await api.post('/reports/bulk-delete', { ids, reason })).data,
    onSuccess: (res, vars) => {
      const n = res?.deleted ?? vars.ids.length
      toast.success(`${n} ${n === 1 ? 'report' : 'reports'} deleted.`)
      bulkDelete?.clearSelection?.()
      setBulkDelete(null)
      setDeleteReason('')
      reportsQuery.refetch()
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const bulkActions = canDeleteReports
    ? [
        {
          label: 'Delete',
          icon: Trash2,
          danger: true,
          onClick: (ids, ctx) => {
            setDeleteReason('')
            setBulkDelete({ ids, clearSelection: ctx?.clearSelection })
          },
        },
      ]
    : []

  // "Period at a glance" KPI cards for the opened WEEKLY report.
  //
  // A weekly report's stored `content` is a snapshot of what the author
  // submitted — editing a daily report inside the same week does not rewrite
  // it, and should not. What DOES move is the period rollup computed from
  // those dailies, which the author sees live on their own report page. The
  // Company Admin Work Reports modal already shows it; this one never did, so
  // a Manager or Team Lead reviewing the same report saw figures that had
  // since gone stale with no way to tell (QA 2026-09-12).
  //
  // `/reports/:id/metrics` was built for exactly this panel — its access is
  // gated by `metricsForReport` -> `findOne`, the same authorization that let
  // this list return the row, so opening it cannot widen what a supervisor
  // can read. Fetched lazily, only when a weekly report is actually opened.
  const weeklyMetricsQuery = useQuery({
    queryKey: ['reports', 'metrics', openReport?.id],
    // Weekly AND monthly reports carry a "period at a glance" rollup (the
    // backend `metricsForReport` supports both); a supervisor previously saw the
    // cards only on weekly, unlike the author page which shows both.
    enabled: Boolean(
      openReport &&
        (openReport.type === 'weekly' || openReport.type === 'monthly') &&
        openReport.id,
    ),
    queryFn: async () => {
      const res = await api.get(`/reports/${openReport.id}/metrics`)
      return {
        keyMetrics: reportKeyMetrics.parse(res.data?.keyMetrics ?? {}),
        // Rolled-up meetings aggregated from the period's dailies (reflects a
        // daily meeting added after the weekly/monthly was filed).
        meetings: Array.isArray(res.data?.meetings) ? res.data.meetings : null,
      }
    },
  })

  // Fetch the whole team report set once; the DataTable does the type /
  // department / period / search filtering, sorting, pagination and export
  // entirely client-side (mirrors the Company Admin Work Reports page). Each
  // report is parsed INDEPENDENTLY so one malformed legacy row can't blow up
  // the list with a thrown ZodError.
  const reportsQuery = useQuery({
    queryKey: ['team-reports'],
    queryFn: async () => {
      const res = await api.get('/reports')
      const raw = Array.isArray(res.data?.data) ? res.data.data : []
      const data = []
      for (const row of raw) {
        const parsed = workReport.safeParse(row)
        if (parsed.success) data.push(parsed.data)
        else
          console.warn(
            '[team reports] skipping malformed report',
            row?.id,
            parsed.error?.issues,
          )
      }
      return { data }
    },
  })

  const rows = reportsQuery.data?.data ?? []

  // Department options for the company-wide filter — the distinct
  // departments present in the fetched reports (mirrors the Leaderboard
  // filter; avoids the admin-gated departments endpoint).
  const deptOptions = useMemo(() => {
    const m = new Map()
    for (const r of reportsQuery.data?.data ?? []) {
      if (r.departmentId) {
        m.set(String(r.departmentId), r.departmentName ?? 'No department')
      }
    }
    return [...m.entries()].map(([value, label]) => ({ value, label }))
  }, [reportsQuery.data])

  const columns = useMemo(
    () => [
      {
        key: 'type',
        header: 'Type',
        minWidth: 90,
        sortAccessor: (r) => (r.type ?? '').toLowerCase(),
        exportValue: (r) => (r.type ?? '').toUpperCase(),
        render: (r) => (
          <span className="text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]">
            {r.type}
          </span>
        ),
      },
      {
        key: 'fullName',
        header: 'Full Name',
        grow: true,
        pinnable: true,
        minWidth: 180,
        sortAccessor: (r) => (r.userFullName ?? '').toLowerCase(),
        searchAccessor: (r) => `${r.userFullName ?? ''} ${r.userEmailId ?? ''}`,
        exportValue: (r) => r.userFullName ?? '',
        render: (r) => (
          <span className="text-sm text-[color:var(--color-ink-strong)] truncate block">
            {r.userFullName}
          </span>
        ),
      },
      {
        key: 'email',
        header: 'Email ID',
        minWidth: 220,
        sortAccessor: (r) => (r.userEmailId ?? '').toLowerCase(),
        exportValue: (r) => r.userEmailId ?? '',
        render: (r) => (
          <span className="text-sm text-[color:var(--color-ink-muted)] truncate block">
            {r.userEmailId ?? '—'}
          </span>
        ),
      },
      // Department column only for company-wide viewers (department-scoped
      // members always see just their own department, so the column is noise).
      ...(canSeeAllDepartments
        ? [
            {
              key: 'department',
              header: 'Department',
              minWidth: 160,
              sortAccessor: (r) => (r.departmentName ?? '').toLowerCase(),
              exportValue: (r) => r.departmentName ?? '',
              render: (r) => (
                <span className="text-sm text-[color:var(--color-ink-muted)] truncate block">
                  {r.departmentName ?? '—'}
                </span>
              ),
            },
          ]
        : []),
      {
        key: 'period',
        header: 'Period',
        minWidth: 170,
        sortAccessor: (r) => (r.periodStart ? new Date(r.periodStart) : null),
        exportValue: (r) => fmtTeamReportPeriod(r),
        render: (r) => (
          <span className="text-sm text-[color:var(--color-ink)] truncate block">
            {fmtTeamReportPeriod(r)}
          </span>
        ),
      },
      {
        key: 'submittedAt',
        header: 'Submitted',
        minWidth: 170,
        sortAccessor: (r) => (r.submittedAt ? new Date(r.submittedAt) : null),
        exportValue: (r) => (r.submittedAt ? formatDateTime(r.submittedAt) : ''),
        render: (r) => (
          <span className="text-xs text-[color:var(--color-ink-muted)] whitespace-nowrap">
            {r.submittedAt ? formatDateTime(r.submittedAt) : '—'}
          </span>
        ),
      },
      {
        key: 'actions',
        header: 'Action',
        pinnedRight: true,
        align: 'center',
        minWidth: 72,
        sortable: false,
        exportable: false,
        hideable: false,
        render: (r) => (
          <RowActionsMenu
            label={`Action for ${r.userFullName}`}
            items={[
              {
                key: 'view',
                label: 'View report',
                icon: Eye,
                onClick: () => setOpenReport(r),
              },
            ]}
          />
        ),
      },
    ],
    [canSeeAllDepartments],
  )

  const filterConfig = useMemo(
    () => [
      {
        key: 'type',
        label: 'Type',
        type: 'radio',
        anyLabel: 'All types',
        filterAccessor: (r) => r.type,
        options: [
          { value: 'daily', label: 'Daily' },
          { value: 'weekly', label: 'Weekly' },
          { value: 'monthly', label: 'Monthly' },
        ],
      },
      ...(canSeeAllDepartments
        ? [
            {
              // `radio`, matching the Type filter above. As a `select` this was
              // the only control on the page that opened a native dropdown,
              // while every other filter was an inline list — one page, two
              // interaction patterns (defect DF_028 / Mantis 22210). The
              // drawer's radio group caps its height and scrolls, so a tenant
              // with many departments still reads fine.
              key: 'department',
              label: 'Department',
              type: 'radio',
              anyLabel: 'All departments',
              filterAccessor: (r) => String(r.departmentId ?? ''),
              options: deptOptions,
            },
          ]
        : []),
      {
        key: 'period',
        label: 'Reporting period',
        type: 'daterange',
        filterAccessor: (r) => (r.periodStart ?? '').slice(0, 10),
        hint: 'Filter by the period a report covers.',
      },
    ],
    [canSeeAllDepartments, deptOptions],
  )

  return (
    <div>
      <Breadcrumb current="Team Reports" homePath="/app" homeLabel="Dashboard" />

      <div className="mb-6">
        <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
          {/* Matches this route’s sidebar icon (DF_029 / Mantis 22222). */}
          <FileBarChart size={20} className="text-[color:var(--color-accent-strong)]" />
          Team Reports
        </h1>
        {/* Short subtitle to match the "Manage every company. N
         *  companies." pattern used on every other panel page. */}
        {/* Only promise a TEAM view to someone who actually has one. */}
        <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
          {canSeeTeamReports
            ? 'Browse daily, weekly, and monthly reports submitted by your team.'
            : 'Reports submitted across your team.'}
        </p>
      </div>

      {!canSeeTeamReports ? (
        <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-10 text-center">
          <FileBarChart
            size={26}
            className="mx-auto mb-3 text-[color:var(--color-ink-muted)]"
          />
          <p className="m-0 text-sm font-medium text-[color:var(--color-ink-strong)]">
            Team Reports isn’t available for your role.
          </p>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            You need department- or company-wide report visibility to browse
            your team’s reports. Your own reports are under Reports.
          </p>
        </div>
      ) : (
      <DataTable
        tableId="employee-team-reports"
        columns={columns}
        rows={rows}
        getRowId={(r) => r.id}
        loading={reportsQuery.isLoading}
        error={
          reportsQuery.isError
            ? extractApiError(reportsQuery.error).message
            : null
        }
        onRetry={() => reportsQuery.refetch()}
        title="Team Reports"
        exportBaseName="team-reports"
        exportTitle="Team Reports"
        selectable
        bulkActions={bulkActions}
        searchPlaceholder="Search by Full Name or Email ID"
        filterConfig={filterConfig}
        defaultSort={[{ key: 'submittedAt', dir: 'desc' }]}
        emptyState={{
          icon: Users,
          title: 'No reports submitted yet',
          body: 'Reports will appear here once your team starts submitting them.',
        }}
      />
      )}

      {/* Bulk delete — reason is mandatory, so this is a small FORM rather
       *  than a ConfirmDialog (whose `message` renders inside a <p>, which
       *  cannot legally contain a textarea). Each report still gets its own
       *  audit row carrying this one reason. */}
      <Modal
        open={Boolean(bulkDelete)}
        onClose={() => {
          if (!bulkDeleteMutation.isPending) setBulkDelete(null)
        }}
        title={`Delete ${bulkDelete?.ids?.length ?? 0} ${
          (bulkDelete?.ids?.length ?? 0) === 1 ? 'report' : 'reports'
        }?`}
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setBulkDelete(null)}
              disabled={bulkDeleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={bulkDeleteMutation.isPending}
              disabled={!deleteReason.trim()}
              onClick={() =>
                bulkDeleteMutation.mutate({
                  ids: bulkDelete.ids,
                  reason: deleteReason.trim(),
                })
              }
            >
              Delete
            </Button>
          </>
        }
      >
        <p className="text-sm text-[color:var(--color-ink)]">
          This permanently removes the selected work reports. An audit record is
          kept for each one, and the employee can file that period again.
        </p>
        <label className="mt-4 block">
          <span className="text-xs font-medium text-[color:var(--color-ink-muted)]">
            Reason (required)
          </span>
          <textarea
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            rows={3}
            maxLength={500}
            autoFocus
            placeholder="e.g. Filed under the wrong date."
            className="mt-1 w-full rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] px-3 py-2 text-sm text-[color:var(--color-ink)]"
          />
        </label>
      </Modal>

      {/* Detail dialog — opens when a row is clicked. Wider than the
       *  default `xl` (max-w-4xl) so the embedded 7-column Work Details
       *  table — especially the wrapping Task/description cell — isn't
       *  cramped. */}
      <Modal
        open={Boolean(openReport)}
        onClose={() => setOpenReport(null)}
        size="xl"
        widthClass="max-w-6xl"
        title={
          openReport
            ? `${openReport.type.charAt(0).toUpperCase()}${openReport.type.slice(1)} report · ${openReport.userFullName}`
            : ''
        }
        description={openReport ? teamReportSubtitle(openReport) : undefined}
        footer={
          <Button variant="secondary" onClick={() => setOpenReport(null)}>
            Close
          </Button>
        }
      >
        {openReport ? (
          <div className="space-y-5">
            {/* Period at a glance — weekly AND monthly, matching the author
             *  page. Live numbers from the period's daily reports (incl. the
             *  meeting count), so a daily edited/added after the report was
             *  filed is reflected here. */}
            {(openReport.type === 'weekly' || openReport.type === 'monthly') &&
            weeklyMetricsQuery.data ? (
              <KeyMetricsPanel
                metrics={weeklyMetricsQuery.data.keyMetrics}
                periodType={openReport.type}
                caption="Computed from daily reports"
              />
            ) : null}
            {openReport.content.summary ? (
              <div>
                <h3 className="text-sm uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1 mt-0">
                  Summary
                </h3>
                <p className="text-sm text-[color:var(--color-ink)] whitespace-pre-wrap">
                  {openReport.content.summary}
                </p>
              </div>
            ) : null}
            <TeamWorkItemsBlock items={openReport.content.workItems} />
            {/* Accomplishments — the Weekly + Monthly forms capture this
             *  section (and often it's the ONLY content on those reports),
             *  but the team viewer used to omit it, so a manager opening a
             *  weekly/monthly report saw a blank dialog. Mirrors the admin
             *  Work Reports modal. TeamListBlock renders nothing when the
             *  array is empty, so daily reports stay clean. */}
            <TeamListBlock
              label="Accomplishments"
              items={openReport.content.accomplishments}
            />
            <TeamBlockersBlock items={openReport.content.blockers} />
            <TeamListBlock
              label="Plans for Next"
              items={openReport.content.plansForNext}
            />
            {/* Weekly/monthly: show the ROLLED-UP meetings (aggregated from the
             *  period's dailies) so a meeting added to a daily after this report
             *  was filed appears; daily reports show their own meetings. Falls
             *  back to the filed snapshot until the rollup loads. */}
            <TeamMeetingsBlock
              items={
                (openReport.type === 'weekly' || openReport.type === 'monthly') &&
                weeklyMetricsQuery.data?.meetings
                  ? weeklyMetricsQuery.data.meetings
                  : openReport.content.meetings
              }
            />
            {openReport.content.notes ? (
              <div>
                <h3 className="text-sm uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1 mt-0">
                  Notes
                </h3>
                <p className="text-sm text-[color:var(--color-ink)] whitespace-pre-wrap">
                  {openReport.content.notes}
                </p>
              </div>
            ) : null}
            {/* Every block above renders nothing when its section is empty.
             *  Without this fallback a report with no content at all would
             *  show a blank dialog body that reads as "broken". */}
            {!teamReportHasContent(openReport.content) ? (
              <p className="text-sm text-[color:var(--color-ink-muted)] italic">
                This report was submitted without any content.
              </p>
            ) : null}
          </div>
        ) : null}
      </Modal>
    </div>
  )
}

/** Local copies of the four detail-modal blocks the Company Admin
 *  Work Reports page renders. Prefixed with `Team` so they don't
 *  collide with any future shared export — if these grow further
 *  they can be lifted into a shared module. */
function TeamListBlock({ label, items }) {
  if (!items || items.length === 0) return null
  return (
    <div>
      <h3 className="text-sm uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1 mt-0">
        {label}
      </h3>
      <ul className="list-disc pl-5 space-y-0.5 text-sm text-[color:var(--color-ink)]">
        {items.map((it, i) => (
          <li key={i}>{it}</li>
        ))}
      </ul>
    </div>
  )
}

function TeamWorkItemsBlock({ items }) {
  if (!items || items.length === 0) return null
  // Time / totalHours column restored 2026-06-01 so managers see the
  // hours worked per task — matching the Company Admin Work Reports
  // modal (WorkReportsPage.jsx), which kept its Time column throughout.
  const totalHours = items.reduce((sum, it) => sum + Number(it.timeSpent ?? 0), 0)
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="text-sm uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] m-0">
          Work Details
        </h3>
        <p className="text-xs text-[color:var(--color-ink-muted)]">
          {items.length} {items.length === 1 ? 'task' : 'tasks'}
          {' · '}
          <span className="font-semibold text-[color:var(--color-ink-strong)] tabular-nums">
            {formatHM(totalHours)}
          </span>{' '}
          total
        </p>
      </div>
      <div className="overflow-x-auto rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)]">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="text-left bg-[color:var(--color-canvas)]">
              <th className="px-4 py-3 text-sm font-medium text-[color:var(--color-ink-muted)]">
                Task
              </th>
              <th className="px-4 py-3 text-sm font-medium text-[color:var(--color-ink-muted)]">
                Project
              </th>
              <th className="px-4 py-3 text-sm font-medium text-[color:var(--color-ink-muted)]">
                Module
              </th>
              <th className="px-4 py-3 text-sm font-medium text-[color:var(--color-ink-muted)]">
                Type
              </th>
              <th className="px-4 py-3 text-sm font-medium text-[color:var(--color-ink-muted)]">
                Status
              </th>
              <th className="px-4 py-3 text-sm font-medium text-[color:var(--color-ink-muted)]">
                Priority
              </th>
              <th className="px-4 py-3 text-sm font-medium text-[color:var(--color-ink-muted)] text-right">
                Time
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr
                key={i}
                className="align-top border-t border-[color:var(--color-rule)]"
              >
                <td className="px-4 py-3 text-[color:var(--color-ink-strong)]">
                  <p className="font-medium">{it.title ?? '—'}</p>
                  {it.description ? (
                    <p className="mt-0.5 text-xs text-[color:var(--color-ink-muted)] whitespace-pre-wrap text-justify">
                      {it.description}
                    </p>
                  ) : null}
                </td>
                <td className="px-4 py-3 text-[color:var(--color-ink)]">
                  {it.project || '—'}
                </td>
                <td className="px-4 py-3 text-[color:var(--color-ink)]">
                  {it.module || '—'}
                </td>
                <td className="px-4 py-3 text-[color:var(--color-ink)] capitalize">
                  {(it.type ?? '').replace(/_/g, ' ') || '—'}
                </td>
                <td className="px-4 py-3 text-[color:var(--color-ink)] capitalize">
                  {(it.status ?? '').replace(/_/g, ' ') || '—'}
                </td>
                <td className="px-4 py-3 text-[color:var(--color-ink)] capitalize">
                  {it.priority || '—'}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-[color:var(--color-ink-strong)]">
                  {formatHM(it.timeSpent)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function TeamBlockersBlock({ items }) {
  if (!items || items.length === 0) return null
  return (
    <div>
      <h3 className="text-sm uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1 mt-0">
        Issues & Dependencies
      </h3>
      <div className="overflow-x-auto rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)]">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="text-left bg-[color:var(--color-canvas)]">
              <th className="px-4 py-3 text-sm font-medium text-[color:var(--color-ink-muted)]">
                Blocker
              </th>
              <th className="px-4 py-3 text-sm font-medium text-[color:var(--color-ink-muted)]">
                Dependency
              </th>
              <th className="px-4 py-3 text-sm font-medium text-[color:var(--color-ink-muted)]">
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((b, i) => (
              <tr
                key={i}
                className="align-top border-t border-[color:var(--color-rule)]"
              >
                <td className="px-4 py-3 text-[color:var(--color-ink-strong)]">
                  {b.blocker || '—'}
                </td>
                <td className="px-4 py-3 text-[color:var(--color-ink)]">
                  {b.dependencyOn || '—'}
                </td>
                <td className="px-4 py-3 text-[color:var(--color-ink)] capitalize">
                  {(b.resolutionStatus ?? '').replace(/_/g, ' ') || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function TeamMeetingsBlock({ items }) {
  if (!items || items.length === 0) return null
  // Date column only when at least one meeting carries one — rollup
  // meetings are stamped with the daily they came from; meetings viewed
  // on a daily report have no date of their own and the column would
  // just be a strip of dashes.
  const hasDates = items.some((m) => m.date)
  return (
    <div>
      <h3 className="text-sm uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-1 mt-0">
        Communication & Collaboration
      </h3>
      <div className="overflow-x-auto rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)]">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="text-left bg-[color:var(--color-canvas)]">
              {hasDates ? (
                <th className="px-4 py-3 text-sm font-medium text-[color:var(--color-ink-muted)] whitespace-nowrap">
                  Date
                </th>
              ) : null}
              <th className="px-4 py-3 text-sm font-medium text-[color:var(--color-ink-muted)]">
                Meeting
              </th>
              <th className="px-4 py-3 text-sm font-medium text-[color:var(--color-ink-muted)] text-right">
                Duration
              </th>
              <th className="px-4 py-3 text-sm font-medium text-[color:var(--color-ink-muted)]">
                Attendees
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((m, i) => (
              <tr
                key={i}
                className="align-top border-t border-[color:var(--color-rule)]"
              >
                {hasDates ? (
                  <td className="px-4 py-3 text-[color:var(--color-ink)] tabular-nums whitespace-nowrap">
                    {m.date ? formatDate(m.date) : '—'}
                  </td>
                ) : null}
                <td className="px-4 py-3 text-[color:var(--color-ink-strong)] font-medium">
                  {m.title || '—'}
                  {/* Legacy reports stored purpose separately — show it as
                   *  subtext so merging the column doesn't hide old data. */}
                  {m.purpose ? (
                    <span className="block font-normal text-[color:var(--color-ink-muted)]">
                      {m.purpose}
                    </span>
                  ) : null}
                </td>
                <td className="px-4 py-3 text-right tabular-nums text-[color:var(--color-ink-strong)]">
                  {formatHM((m.durationMinutes ?? 0) / 60)}
                </td>
                <td className="px-4 py-3 text-[color:var(--color-ink)]">
                  {m.attendees || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ============================================================================
//   DAILY CORPORATE REPORT — new 6-section form
// ============================================================================

/**
 * Fallback dropdown options used while the tenant's Work Report Settings
 * are still loading, OR when the tenant has configured zero options.
 *
 * Task Type has NO fallback — the catalogue is company-specific and
 * intentionally starts EMPTY (a Manager / Company Admin builds it on the
 * Report Settings page). Until they do, the row's Type dropdown simply
 * has no options rather than presuming a (dev-centric) default set.
 *
 * Item Status keeps a universal safety net (every task tracker has these
 * states) so the Status dropdown is never empty.
 *
 * The live source of truth is `GET /work-report-settings` (see
 * `useWorkReportOptions` below), which a manager edits via the
 * "Report Settings" page in the Manager sidebar section.
 */
const FALLBACK_TASK_TYPE_OPTIONS = []

const FALLBACK_ITEM_STATUS_OPTIONS = [
  { value: 'in_progress', label: 'In progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'blocked', label: 'Blocked' },
]

/** Shared hook for the per-tenant Task Type + Item Status option
 *  lists. Wraps useQuery so every WorkRow instance dedupes onto a
 *  single in-flight request via React Query's cache. Returns the
 *  fetched options when available, falling back to the hardcoded
 *  defaults so the form never renders an empty dropdown — see the
 *  long comment on the FALLBACK_* constants above for the
 *  rationale. */
function useWorkReportOptions() {
  const query = useQuery({
    queryKey: ['work-report-settings'],
    queryFn: async () => {
      const res = await api.get('/work-report-settings')
      return res.data?.data ?? res.data
    },
    // Settings change rarely; cache for 5 minutes so the dozen
    // WorkRow instances on a single report page don't refetch on
    // every re-render or quick navigation back.
    staleTime: 5 * 60_000,
  })
  const data = query.data
  const taskTypeOptions =
    Array.isArray(data?.taskTypeOptions) && data.taskTypeOptions.length > 0
      ? data.taskTypeOptions
      : FALLBACK_TASK_TYPE_OPTIONS
  const itemStatusOptions =
    Array.isArray(data?.itemStatusOptions) && data.itemStatusOptions.length > 0
      ? data.itemStatusOptions
      : FALLBACK_ITEM_STATUS_OPTIONS
  // Project + Module are free-text on the report row (the reporter types
  // them), so no option catalogues are fetched here anymore.
  return { taskTypeOptions, itemStatusOptions }
}

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
]

const RESOLUTION_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'awaiting', label: 'Awaiting' },
  { value: 'in_review', label: 'In review' },
  { value: 'resolved', label: 'Resolved' },
]

function emptyWorkItem(taskId = null) {
  return {
    taskId,
    title: '',
    description: '',
    project: '',
    module: '',
    // No presumed Task Type — the catalogue starts empty and is
    // company-specific; the reporter picks from the configured options
    // (or leaves it blank until a Manager sets the catalogue up).
    type: '',
    status: 'in_progress',
    priority: 'medium',
    timeSpent: 0,
  }
}

function emptyBlocker() {
  return { blocker: '', dependencyOn: '', resolutionStatus: 'open' }
}

// ---------- Local-draft persistence ----------
//
// Keep an unsubmitted draft alive in localStorage so navigating away
// from the daily report and coming back doesn't blow away what the user
// already typed. The key is per-user + per-date so:
//   - Logging in as someone else doesn't surface their drafts.
//   - Switching dates inside the page picks up that date's own draft.
//
// We only ever read the draft when there is NO submitted report on the
// server for that date — once submitted, the server is the source of
// truth and the draft is wiped.
//
// Drafts older than 30 days are evicted on read so localStorage doesn't
// accumulate dead entries from users who started forms but never
// finished them.
const DRAFT_TTL_MS = 30 * 24 * 60 * 60 * 1000

function dailyDraftKey(userId, dateIso) {
  return `taskmagix-daily-draft:${userId}:${dateIso}`
}

function loadDailyDraft(userId, dateIso) {
  if (!userId) return null
  try {
    const raw = window.localStorage.getItem(dailyDraftKey(userId, dateIso))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || !parsed.content) return null
    if (
      typeof parsed.savedAt === 'number' &&
      Date.now() - parsed.savedAt > DRAFT_TTL_MS
    ) {
      window.localStorage.removeItem(dailyDraftKey(userId, dateIso))
      return null
    }
    return parsed.content
  } catch {
    return null
  }
}

function saveDailyDraft(userId, dateIso, content) {
  if (!userId) return
  try {
    window.localStorage.setItem(
      dailyDraftKey(userId, dateIso),
      JSON.stringify({ savedAt: Date.now(), content }),
    )
  } catch {
    // Quota exceeded or storage unavailable (some private modes) —
    // silently no-op; the form still works, just without a draft.
  }
}

function clearDailyDraft(userId, dateIso) {
  if (!userId) return
  try {
    window.localStorage.removeItem(dailyDraftKey(userId, dateIso))
  } catch {
    /* noop */
  }
}

function emptyMeeting() {
  // `purpose` is no longer collected (merged into the single Meeting field);
  // it stays optional in the schema so legacy reports still validate.
  return { title: '', durationMinutes: 0, attendees: '' }
}

function fmtDailyHeader(iso) {
  // `iso` is a "YYYY-MM-DD" day key — formatPattern treats it as a calendar day.
  return formatPattern(iso, 'EEEE, dd MMMM yyyy')
}

/** Step the day +/- 1 in ISO (YYYY-MM-DD) without crossing a TZ boundary. */
function shiftIso(iso, days) {
  const [y, m, d] = iso.split('-').map((s) => parseInt(s, 10))
  const dt = new Date(Date.UTC(y, m - 1, d))
  dt.setUTCDate(dt.getUTCDate() + days)
  return isoFromDate(dt)
}

export function DailyCorporateReportPage() {
  const qc = useQueryClient()
  const user = useAuthStore((s) => s.user)
  // Schedule-aware deadline + active-day check. The Company Admin's
  // Report Schedule decides when daily reports are due (default
  // 5:30 PM) and which weekdays the cadence covers (default Mon-Fri).
  // The page still lets the user pick any date — we just surface
  // the deadline + a "not an active day" hint when they pick one
  // outside the cadence.
  const schedule = useReportSchedule()
  // `?date=YYYY-MM-DD` deep-links the form to a specific day — the
  // dashboard's "yesterday's daily wasn't submitted" CTA uses it. Only
  // honoured when it's a valid, in-range date (not future, not older
  // than the 365-day backfill window the picker itself enforces).
  const [searchParams] = useSearchParams()
  const [anchor, setAnchor] = useState(() => {
    const qp = searchParams.get('date')
    const today = todayIso()
    if (
      qp &&
      /^\d{4}-\d{2}-\d{2}$/.test(qp) &&
      qp <= today &&
      qp >= shiftIso(today, -365)
    ) {
      return qp
    }
    return today
  })
  const [content, setContent] = useState(emptyContent())
  // Tracks WHICH (user, date) the current `content` was loaded for.
  // The save-effect only writes to localStorage when this matches the
  // CURRENT anchor — otherwise we'd persist the previous date's
  // content under the new date's key during the date-picker swap,
  // corrupting the new date's draft slot. */
  const [hydratedFor, setHydratedFor] = useState(null)

  // Existing submission lookup — same query the legacy form uses, kept on
  // its own queryKey so toggling between dates doesn't thrash the cache.
  const existingQuery = useQuery({
    queryKey: ['reports', 'daily', anchor, anchor, user?.id],
    queryFn: async () => {
      const res = await api.get('/reports', {
        params: { type: 'daily', from: anchor, to: anchor, userId: user?.id },
      })
      const parsed = workReportsResponse.parse(res.data)
      // `.find` is `undefined` when no report exists for this date yet —
      // React Query rejects an `undefined` queryFn result ("Query data
      // cannot be undefined"). Return `null`; the hydration effect below
      // already treats a falsy row as "no server report".
      return parsed.data.find((r) => r.periodStart.slice(0, 10) === anchor) ?? null
    },
    enabled: Boolean(user),
  })

  // Load-effect — runs whenever the (anchor, user) pair we're showing
  // hasn't yet been hydrated. Won't re-run while we're still
  // displaying the same date.
  useEffect(() => {
    if (!user?.id) return
    if (hydratedFor === `${user.id}:${anchor}`) return

    const row = existingQuery.data
    if (row) {
      // A submitted (or AI-drafted) report for this date already exists
      // on the server — the server is the truth. Any local draft is
      // stale and gets cleared so we don't accidentally restore an old
      // edit on a date that's since been submitted.
      setContent({
        summary: row.content.summary ?? '',
        workItems: row.content.workItems ?? [],
        accomplishments: row.content.accomplishments ?? [],
        blockers: row.content.blockers ?? [],
        plansForNext: row.content.plansForNext ?? [],
        meetings: row.content.meetings ?? [],
        notes: row.content.notes ?? '',
        hoursLogged: row.content.hoursLogged,
        taskIds: row.content.taskIds ?? [],
      })
      clearDailyDraft(user.id, anchor)
      setHydratedFor(`${user.id}:${anchor}`)
    } else if (!existingQuery.isLoading) {
      // No server-side report yet — restore the in-progress local draft
      // if one is sitting in localStorage. Falls back to the empty
      // template only when there's truly nothing to recover.
      const draft = loadDailyDraft(user.id, anchor)
      if (draft) {
        setContent({ ...emptyContent(), ...draft })
      } else {
        setContent(emptyContent())
      }
      setHydratedFor(`${user.id}:${anchor}`)
    }
  }, [existingQuery.data, existingQuery.isLoading, anchor, user?.id, hydratedFor])

  // Auto-save the in-flight content to localStorage on every edit so a
  // user who navigates away (clicks a sidebar link, opens a sub-task,
  // refreshes the tab) can come back to exactly what they had typed.
  //
  // CRITICAL guard: only save when the `content` we're holding was
  // loaded for the CURRENT (user, anchor). Otherwise switching the date
  // picker would write the previous date's content into the new date's
  // localStorage slot before the load-effect has a chance to populate
  // the new one. */
  useEffect(() => {
    if (!user?.id) return
    if (hydratedFor !== `${user.id}:${anchor}`) return
    if (existingQuery.data?.submittedAt) return
    saveDailyDraft(user.id, anchor, content)
  }, [content, anchor, user?.id, existingQuery.data?.submittedAt, hydratedFor])

  const autoFillQuery = useQuery({
    queryKey: ['reports', 'daily', 'auto-fill-counts', anchor],
    queryFn: async () => {
      const res = await api.post('/reports/daily/auto-fill', { date: anchor })
      return res.data
    },
    // Only fetch the counts card, not the full payload, when the user opens
    // the page; the content is only applied when they click "Auto-fill".
    enabled: Boolean(user),
  })

  // The Work Details "Task" column is a plain manual text field — the
  // reporter types the task name themselves. The earlier space-sourced
  // picker (a native <datalist> of the user's open tasks) was removed so
  // entry is manual across every panel.

  // Glance-card counts. The two STATUS cards ("closed" / "in progress")
  // live-derive from the WORK DETAILS rows the user is actually logging,
  // so they move the moment a row's status changes — that's the behaviour
  // users expect when they add/edit a task row. "Due tomorrow" / "Overdue"
  // have no row-level equivalent (rows carry no due date), so they stay
  // sourced from the deterministic server task summary.
  const serverCounts = autoFillQuery.data?.counts ?? null
  const glanceCounts = useMemo(() => {
    const rows = (content.workItems ?? []).filter(
      (it) => (it.title ?? '').trim().length > 0,
    )
    return {
      tasksClosed: rows.filter((it) => it.status === 'completed').length,
      tasksInProgress: rows.filter((it) => it.status === 'in_progress').length,
      tasksDueTomorrow: serverCounts?.tasksDueTomorrow ?? 0,
      tasksOverdue: serverCounts?.tasksOverdue ?? 0,
    }
  }, [content.workItems, serverCounts])

  const autoFillMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post('/reports/daily/auto-fill', { date: anchor })
      return res.data
    },
    onSuccess: (payload) => {
      setContent((c) => ({
        ...c,
        // Replace structured sections; keep user-typed accomplishments and
        // notes intact unless they're empty.
        workItems: payload.content.workItems,
        accomplishments:
          c.accomplishments.length > 0
            ? c.accomplishments
            : payload.content.accomplishments,
        blockers: c.blockers.length > 0 ? c.blockers : payload.content.blockers,
        plansForNext:
          c.plansForNext.length > 0
            ? c.plansForNext
            : payload.content.plansForNext,
        taskIds: payload.content.taskIds,
      }))
      toast.success(
        `Auto-filled ${payload.content.workItems.length} task${payload.content.workItems.length === 1 ? '' : 's'} from today's activity.`,
      )
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  const submitMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post('/reports/daily', {
        date: anchor,
        content: cleanContentForSubmit(content),
      })
      return workReport.parse(res.data)
    },
    onSuccess: () => {
      toast.success('Report submitted.')
      // Server-side record is now the truth — drop the local draft so
      // re-opening the page doesn't show a stale unsaved-changes echo.
      clearDailyDraft(user?.id, anchor)
      void qc.invalidateQueries({ queryKey: ['reports'] })
      // The Home dashboard's Daily Report card reads submission state
      // from this key — refresh it directly so the flip to "Submitted"
      // doesn't depend on the socket being connected.
      void qc.invalidateQueries({ queryKey: ['me-dashboard-overview'] })
    },
    onError: (err) => toast.error(reportErrorMessage(err)),
  })

  const isSubmitted = Boolean(existingQuery.data?.submittedAt)

  // Lower bound for the date picker: a generous 1-year backfill window.
  // A daily report can be logged for today or any recent past day, but
  // "unreasonably old" dates (e.g. 1990) are nonsensical — they aren't a
  // reporting period, just a misclick/paste. (Older reports stay viewable
  // through Team Reports / admin Work Reports.) Adjust the day count here
  // if the team needs a longer backfill window.
  const minDateIso = shiftIso(todayIso(), -365)

  function patchWorkItem(idx, patch) {
    setContent((c) => ({
      ...c,
      workItems: c.workItems.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    }))
  }
  function removeWorkItem(idx) {
    setContent((c) => ({
      ...c,
      workItems: c.workItems.filter((_, i) => i !== idx),
    }))
  }
  function addWorkItem() {
    setContent((c) => ({ ...c, workItems: [...c.workItems, emptyWorkItem()] }))
  }

  function patchBlocker(idx, patch) {
    setContent((c) => ({
      ...c,
      blockers: c.blockers.map((b, i) => (i === idx ? { ...b, ...patch } : b)),
    }))
  }
  function removeBlocker(idx) {
    setContent((c) => ({ ...c, blockers: c.blockers.filter((_, i) => i !== idx) }))
  }
  function addBlocker() {
    setContent((c) => ({ ...c, blockers: [...c.blockers, emptyBlocker()] }))
  }

  function patchMeeting(idx, patch) {
    setContent((c) => ({
      ...c,
      meetings: c.meetings.map((m, i) => (i === idx ? { ...m, ...patch } : m)),
    }))
  }
  function removeMeeting(idx) {
    setContent((c) => ({ ...c, meetings: c.meetings.filter((_, i) => i !== idx) }))
  }
  function addMeeting() {
    setContent((c) => ({ ...c, meetings: [...c.meetings, emptyMeeting()] }))
  }

  function updateBullet(
    key,
    idx,
    value,
  ) {
    setContent((c) => ({
      ...c,
      [key]: c[key].map((b, i) => (i === idx ? value : b)),
    }))
  }
  function removeBullet(key, idx) {
    setContent((c) => ({ ...c, [key]: c[key].filter((_, i) => i !== idx) }))
  }
  function addBullet(key) {
    setContent((c) => ({ ...c, [key]: [...c[key], ''] }))
  }

  return (
    <div>
      <Breadcrumb current="Daily Report" homePath="/app" homeLabel="Dashboard" />

      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div className="flex items-start gap-3">
          <span className="h-12 w-12 rounded-xl bg-[#D1FAE5] text-[#059669] border border-[#A7F3D0] flex items-center justify-center shrink-0">
            <ClipboardList size={24} />
          </span>
          <div>
            <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)]">
              Daily Report
            </h1>
            <p className="mt-1 text-[color:var(--color-ink-muted)]">
              {fmtDailyHeader(anchor)}
              {' · '}Submitted by {user?.fullName ?? 'You'}
              {existingQuery.data?.submittedAt
                ? ` · Last edit ${formatDateTime(existingQuery.data.submittedAt)}`
                : ''}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 justify-end">
          {/* Schedule-aware status pills.
           *
           *   • Submitted / Not submitted — primary status.
           *   • "Submit by HH:MM" — deadline from the admin's
           *     Report Schedule (only when daily cadence is enabled).
           *   • "Overdue" — deadline already passed and the user
           *     hasn't submitted yet (only renders for today's
           *     anchor or older — never for future dates).
           *   • "Off-day" — the picked date isn't in the admin's
           *     dailyActiveDays list (e.g. a weekend on a Mon–Fri
           *     cadence). Informational only; submit still works
           *     since users sometimes need to file ad-hoc reports
           *     for non-cadence days. */}
          {isSubmitted ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold bg-[color:var(--color-success-bg)] text-[color:var(--color-success)] border border-[color:var(--color-success-border)]">
              <CheckCircle2 size={14} />
              Submitted
            </span>
          ) : schedule.isDailyActiveDay(anchor) ? (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)] border border-[color:var(--color-warning-border)]">
              <AlertCircle size={14} />
              Not submitted
            </span>
          ) : null}
          {/* Deadline + Overdue apply only on ACTIVE working days. An off-day
           *  (e.g. Sunday on a Mon–Sat cadence) has no daily report due, so it
           *  must never read as "Not submitted", carry a "Submit by" deadline,
           *  or show "Overdue" — only the neutral "Off-day" badge below.
           *  Ad-hoc submission still works. QA 2026-09-02. */}
          {schedule.dailyEnabled && schedule.isDailyActiveDay(anchor) ? (
            (() => {
              const deadline = schedule.dailyDeadlineFor(anchor)
              const overdue =
                !isSubmitted &&
                deadline.getTime() < new Date().getTime() &&
                anchor <= todayIso()
              return (
                <>
                  <span
                    className={
                      'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ' +
                      (overdue
                        ? 'bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)] border-[color:var(--color-warning-border)]'
                        : 'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border-[color:var(--color-accent-border)]')
                    }
                  >
                    Submit by {formatTimeOnly(schedule.dailyReminderAt)}
                  </span>
                  {overdue ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-[color:var(--color-priority-urgent-bg)] text-[color:var(--color-priority-urgent)] border border-[color:var(--color-priority-urgent-border)]">
                      Overdue
                    </span>
                  ) : null}
                </>
              )
            })()
          ) : null}
          {!schedule.isDailyActiveDay(anchor) ? (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)]">
              Off-day
            </span>
          ) : null}
        </div>
      </div>

      {/* Date nav */}
      <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-3 mb-4 flex items-center gap-2">
        <button
          type="button"
          onClick={() =>
            setAnchor((iso) => {
              const prev = shiftIso(iso, -1)
              return prev < minDateIso ? minDateIso : prev
            })
          }
          // Can't step below the backfill floor.
          disabled={anchor <= minDateIso}
          className="h-9 w-9 rounded-md text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[color:var(--color-ink-muted)]"
          aria-label="Previous day"
        >
          <ChevronLeft size={16} />
        </button>
        <input
          type="date"
          value={anchor}
          // Daily reports can only be logged for today or earlier — there's
          // nothing to report for a day that hasn't happened — and not for
          // unreasonably old dates. `min`/`max` grey out out-of-range days in
          // the native picker; the onChange clamp is the belt-and-braces
          // guard for a manually typed/pasted out-of-range date.
          min={minDateIso}
          max={todayIso()}
          onChange={(e) => {
            const next = e.target.value
            if (!next) return
            const clamped =
              next > todayIso()
                ? todayIso()
                : next < minDateIso
                  ? minDateIso
                  : next
            setAnchor(clamped)
          }}
          className="h-9 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-sm text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] tabular-nums"
        />
        <button
          type="button"
          onClick={() => setAnchor((iso) => shiftIso(iso, 1))}
          // Can't step past today — the future has no report to show.
          disabled={anchor >= todayIso()}
          className="h-9 w-9 rounded-md text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[color:var(--color-ink-muted)]"
          aria-label="Next day"
        >
          <ChevronRight size={16} />
        </button>
        <button
          type="button"
          onClick={() => setAnchor(todayIso())}
          // Redundant while already viewing today — disabled, matching
          // the Next-day chevron's convention (QA).
          disabled={anchor >= todayIso()}
          className="ml-auto h-9 px-3 rounded-md text-sm font-medium border border-[color:var(--color-rule)] text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[color:var(--color-ink-muted)]"
        >
          Jump to today
        </button>
      </div>

      {/* Date-aware glance card + Auto-fill */}
      <GlanceCard
        counts={glanceCounts}
        loading={autoFillQuery.isLoading}
        anchor={anchor}
        onAutoFill={() => autoFillMutation.mutate()}
        autoFilling={autoFillMutation.isPending}
      />

      {/* §1 Work details */}
      <WorkDetailsSection
        items={content.workItems}
        meetingHours={
          (content.meetings ?? []).reduce(
            (s, m) => s + Number(m.durationMinutes ?? 0),
            0,
          ) / 60
        }
        onPatch={patchWorkItem}
        onRemove={removeWorkItem}
        onAdd={addWorkItem}
      />

      {/* Communication & Collaboration — placed directly below Work
       *  Details so the two detailed tables read together. */}
      <MeetingsSection
        items={content.meetings}
        onPatch={patchMeeting}
        onRemove={removeMeeting}
        onAdd={addMeeting}
      />

      {/* §2 Achievements — hidden by request. The underlying
       *  `content.accomplishments` array, helpers (addBullet /
       *  removeBullet / updateBullet) and submit payload are
       *  untouched, so re-enabling is just restoring this block. */}

      {/* §3 Blockers */}
      <BlockersSection
        items={content.blockers}
        onPatch={patchBlocker}
        onRemove={removeBlocker}
        onAdd={addBlocker}
      />

      {/* §4 Planning */}
      <BulletSection
        title="Plan for Tomorrow"
        helper="What you'll work on tomorrow."
        items={content.plansForNext}
        onUpdate={(i, v) => updateBullet('plansForNext', i, v)}
        onRemove={(i) => removeBullet('plansForNext', i)}
        onAdd={() => addBullet('plansForNext')}
      />

      {/* Notes */}
      <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4 mt-4">
        <h3 className="text-sm uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-2 mt-0">
          Notes
        </h3>
        {/* min/max-height caps the drag-resize so the field can't be
         *  stretched past a usable height and wreck the layout (QA). */}
        <textarea
          value={content.notes ?? ''}
          onChange={(e) => setContent((c) => ({ ...c, notes: e.target.value }))}
          rows={3}
          placeholder="Enter the Notes"
          className="w-full min-h-[80px] max-h-[300px] px-3 py-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-sm text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] resize-y"
        />
      </div>

      <div className="mt-6 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => {
            // Block submit if any row has hours/details but no task name —
            // it would otherwise be silently dropped, losing those hours.
            const offending = (content.workItems ?? []).filter(
              workRowMissingTitle,
            )
            if (offending.length > 0) {
              toast.error(
                'Add a task name to every work row that has hours or other details.',
              )
              return
            }
            // Same rule for blockers: a row with a status/dependency but
            // no blocker text must be described, not silently dropped.
            if ((content.blockers ?? []).some(blockerRowMissingText)) {
              toast.error(
                'Describe the blocker in every row that has a dependency or status set.',
              )
              return
            }
            // The day's logged time — task rows PLUS meeting minutes,
            // matching the server's computedHours — is capped at the
            // 12h ceiling (incl. overtime, QA ruling 2026-07-22).
            const totalHours =
              (content.workItems ?? []).reduce(
                (s, it) => s + Number(it.timeSpent ?? 0),
                0,
              ) +
              (content.meetings ?? []).reduce(
                (s, m) => s + Number(m.durationMinutes ?? 0),
                0,
              ) / 60
            if (totalHours > MAX_DAILY_HOURS) {
              toast.error(
                `Total logged time is ${formatHM(totalHours)} — a single day can’t exceed ${MAX_DAILY_HOURS} working hours (including overtime).`,
              )
              return
            }
            // Re-submit with nothing changed is a no-op on the server (the
            // upsert just bumps submittedAt) and a hollow "success" to the
            // user. Tell them there's nothing new instead of firing it.
            if (
              isSubmitted &&
              existingQuery.data &&
              reportContentSig(content) ===
                reportContentSig(existingQuery.data.content)
            ) {
              toast('No changes since your last submission.')
              return
            }
            submitMutation.mutate()
          }}
          disabled={submitMutation.isPending}
          className="inline-flex items-center gap-1.5 h-10 px-5 rounded-full text-sm font-semibold bg-[color:var(--color-primary)] text-white shadow-sm hover:shadow hover:bg-[color:var(--color-primary-hover)] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitMutation.isPending ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <CheckCircle2 size={14} />
          )}
          {submitMutation.isPending
            ? 'Submitting…'
            : isSubmitted
              ? // Dailies stay amendable all day (weekly / monthly LOCK on
                // submit — see ReportSubmitPage). "Update report" states
                // that intent; QA flagged "Re-submit" as implying a
                // duplicate submission.
                'Update report'
              : 'Submit report'}
        </button>
      </div>
    </div>
  )
}

// ---------- §0 Today at a glance card ----------

function GlanceCard({
  counts,
  loading,
  anchor,
  onAutoFill,
  autoFilling,
}) {
  // The card always shows counts for the SELECTED date (`anchor`), not
  // necessarily today. Title + stat labels reflect that so a user
  // looking at a previous date doesn't think the numbers are stale.
  const isToday = anchor === todayIso()
  const anchorLabel = formatPattern(anchor, 'EEE dd MMM')
  const nextDayLabel = (() => {
    const next = shiftIso(anchor, 1)
    return formatPattern(next, 'EEE dd MMM')
  })()

  return (
    <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] m-0">
            {isToday ? 'Today at a Glance' : 'At a Glance'}
          </h3>
          {!isToday ? (
            <p className="text-sm text-[color:var(--color-ink-muted)] mt-0.5">
              Counts for {anchorLabel}
            </p>
          ) : null}
        </div>
        {/* "Auto-fill from tasks" (magic-wand) button removed —
         *  reads as an AI affordance and the user wants AI usage
         *  hidden from the work-report flow. The underlying
         *  autoFillMutation + endpoint stay in place so the feature
         *  can be re-enabled by restoring this button.
         *
         *  Touch the props so the destructure stays useful and so
         *  ESLint doesn't strip them from the signature on a
         *  later autoformat pass. */}
        {(() => {
          void onAutoFill
          void autoFilling
          return null
        })()}
      </div>
      {loading ? (
        <p className="text-sm text-[color:var(--color-ink-muted)]">
          Loading task summary…
        </p>
      ) : counts ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <GlanceStat
            label={isToday ? 'Tasks closed today' : `Closed on ${anchorLabel}`}
            value={counts.tasksClosed}
          />
          <GlanceStat
            label={isToday ? 'In progress today' : `Active on ${anchorLabel}`}
            value={counts.tasksInProgress}
          />
          <GlanceStat
            label={isToday ? 'Due tomorrow' : `Due ${nextDayLabel}`}
            value={counts.tasksDueTomorrow}
          />
          <GlanceStat
            label={isToday ? 'Overdue today' : `Overdue by ${anchorLabel}`}
            value={counts.tasksOverdue}
            tone={counts.tasksOverdue > 0 ? 'warning' : undefined}
          />
        </div>
      ) : (
        <p className="text-sm text-[color:var(--color-ink-muted)]">
          No task activity to summarise.
        </p>
      )}
    </div>
  )
}

function GlanceStat({
  label,
  value,
  tone,
}) {
  return (
    <div className="rounded-md bg-[color:var(--color-canvas)] border border-[color:var(--color-rule)] px-3 py-2">
      <p
        className={
          'text-2xl font-semibold tabular-nums ' +
          (tone === 'warning' && value > 0
            ? 'text-[color:var(--color-priority-urgent)]'
            : 'text-[color:var(--color-ink-strong)]')
        }
      >
        {value}
      </p>
      <p className="text-sm text-[color:var(--color-ink-muted)]">{label}</p>
    </div>
  )
}

// ---------- §1 Work details ----------

function WorkDetailsSection({
  items,
  meetingHours = 0,
  onPatch,
  onRemove,
  onAdd,
}) {
  const titledCount = items.filter(
    (it) => (it.title ?? '').trim().length > 0,
  ).length
  // The day's total is task rows PLUS meeting time — mirrors the server
  // (`computedHours` folds meeting minutes into hoursLogged), so this
  // header, the 12h ceiling check, and what submit stores all agree.
  const taskHours = items.reduce((s, it) => s + Number(it.timeSpent ?? 0), 0)
  const totalHours = taskHours + meetingHours
  const overDay = totalHours > MAX_DAILY_HOURS
  // Scroll container for the wide task table — driven by the floating
  // StickyTimelineScrollbar below (same pattern as Team Tasks / the Gantt
  // views), so the table itself shows no native scrollbars.
  const rowsRef = useRef(null)
  return (
    <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4 mb-4">
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="text-sm uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] m-0">
          Work Details
          <span className="text-[color:var(--color-priority-urgent)] ml-0.5">*</span>
        </h3>
        {/* Count only titled rows (blank ones are stripped on submit) and
         *  surface the running time total against the daily ceiling. */}
        <p className="text-sm text-[color:var(--color-ink-muted)]">
          {`${titledCount} task${titledCount === 1 ? '' : 's'} · `}
          <span
            title={
              meetingHours > 0
                ? `${formatHM(taskHours)} tasks + ${formatHM(meetingHours)} meetings`
                : undefined
            }
            className={
              'tabular-nums ' +
              (overDay
                ? 'font-semibold text-[color:var(--color-priority-urgent)]'
                : 'text-[color:var(--color-ink-strong)]')
            }
          >
            {formatHM(totalHours)}
          </span>
          {` / ${MAX_DAILY_HOURS}h`}
        </p>
      </div>
      <p className="text-sm text-[color:var(--color-ink-muted)] mb-3">
        Add one row per task you worked on today. Log the time spent on
        each as hours and minutes.
      </p>
      {overDay ? (
        <p className="text-xs text-[color:var(--color-priority-urgent)] mb-3">
          Total logged time is {formatHM(totalHours)} — a single day can’t
          exceed {MAX_DAILY_HOURS} working hours (including overtime). Trim
          some rows before submitting.
        </p>
      ) : null}

      {items.length === 0 ? (
        <p className="text-sm text-[color:var(--color-ink-muted)] italic mb-3">
          No tasks yet.
        </p>
      ) : (
        <div
          ref={rowsRef}
          // Horizontal scrolling only, with NO native scrollbar at any width —
          // the floating StickyTimelineScrollbar below drives it, matching the
          // Team Tasks / Gantt views. Previously this was `overflow-auto` with
          // `max-h-[70vh]`, which boxed the table in its own scroll pane and
          // drew BOTH an inner vertical and an inner horizontal bar; the page
          // now scrolls vertically as one, so the rows are never trapped in a
          // short window.
          className="overflow-x-auto view-hscroll-sticky -mx-4"
        >
          <table
            className="w-full text-sm border-separate border-spacing-0"
            style={{ minWidth: WORK_TABLE_MIN_WIDTH }}
          >
            <thead>
              <tr className="text-left">
                {/* "Time (h)" column restored 2026-06-01 — Work Reports
                 *  must surface actual hours worked per task. The
                 *  `timeSpent` field, the backend `hoursLogged` sum, and
                 *  the admin modal's Time column / total all still exist;
                 *  this input is the single point where the value enters
                 *  the system. */}
                {[
                  'Task',
                  'Project',
                  'Module',
                  'Type',
                  'Status',
                  'Priority',
                  'Time',
                  '',
                ].map((h, i) => (
                  <th
                    key={i}
                    className="px-2 py-2 text-sm font-medium text-[color:var(--color-ink-muted)] border-b border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] sticky top-0 z-10"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((it, i) => (
                <WorkRow
                  key={i}
                  item={it}
                  index={i}
                  onPatch={(patch) => onPatch(i, patch)}
                  onRemove={() => onRemove(i)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* Floating horizontal scrollbar pinned to the viewport bottom — the
       *  single scroll affordance for the wide table above, matching Team
       *  Tasks and the Gantt views. Width tracks the table's `min-w`. */}
      {items.length > 0 ? (
        <StickyTimelineScrollbar
          timelineRef={rowsRef}
          timelineWidth={WORK_TABLE_MIN_WIDTH}
        />
      ) : null}

      <div className="mt-3">
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border border-[color:var(--color-rule)] text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
        >
          <Plus size={13} /> Add Row
        </button>
      </div>
    </div>
  )
}

function WorkRow({
  item,
  index,
  onPatch,
  onRemove,
}) {
  // Pulls the tenant-configured Task Type + Item Status option
  // lists. React Query dedupes the underlying GET across every
  // WorkRow on the page, so a 20-row report fires ONE request.
  const { taskTypeOptions, itemStatusOptions } = useWorkReportOptions()
  // Wheel-to-adjust for the FOCUSED hours input (quarter-hour steps).
  // Browsers' native number-input wheel behaviour is inconsistent — in
  // this app the wheel scrolled the PAGE instead of nudging the value
  // (QA-reported), making hours fiddly to set. A NATIVE non-passive
  // listener is required: React's synthetic onWheel (and root/window
  // listeners) are passive, so preventDefault can't stop the page
  // scroll from there. Contract matches native number inputs at their
  // best: the wheel only adjusts while the field is FOCUSED — hovering
  // an unfocused input never hijacks page scrolling.
  const isLinked = Boolean(item.taskId)
  // Time is entered as hours + minutes; the stored value stays decimal hours.
  const { h: timeH, m: timeM } = hoursToHM(item.timeSpent)
  // Hide the native number spinner — it overlaps the right edge of these narrow
  // h+m fields and clipped two-digit values (e.g. "30" showed as "3("). Same
  // inline-utility pattern used elsewhere for raw number inputs (GoalDetailPage).
  const hmInputClass =
    'h-8 w-14 px-2 rounded border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-sm text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none'
  const editableClass =
    'h-8 w-full px-2 rounded border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-sm text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)]'

  // A row is "started" once it carries any data beyond the defaults. Such
  // a row REQUIRES a task name — otherwise it's silently dropped on submit
  // (cleanContentForSubmit filters blank titles), so the hours the user
  // logged would vanish without warning. Flag the missing title inline.
  const titleMissing = workRowMissingTitle(item)
  const titleClass =
    'h-8 w-full px-2 rounded border bg-[color:var(--color-canvas)] text-sm text-[color:var(--color-ink)] outline-none ' +
    (titleMissing
      ? 'border-[color:var(--color-priority-urgent)] focus:border-[color:var(--color-priority-urgent)]'
      : 'border-[color:var(--color-rule)] focus:border-[color:var(--color-accent)]')

  return (
    <tr className="align-top hover:bg-[color:var(--color-card-hover)]">
      <td className="px-2 py-2 border-b border-[color:var(--color-rule)] min-w-[400px]">
        <div className="flex items-start gap-1.5">
          {isLinked ? (
            <span title="Linked to a TaskMagix task">
              <Link2 size={12} className="text-[color:var(--color-accent)] mt-2 shrink-0" />
            </span>
          ) : null}
          <div className="flex-1 min-w-0 space-y-1">
            <input
              value={item.title}
              onChange={(e) => onPatch({ title: e.target.value })}
              placeholder="Enter a task name"
              aria-invalid={titleMissing}
              className={titleClass}
            />
            {titleMissing ? (
              <p className="text-xs text-[color:var(--color-priority-urgent)]">
                Task Name is required when a row has hours or other details.
              </p>
            ) : null}
            <textarea
              value={item.description ?? ''}
              onChange={(e) => onPatch({ description: e.target.value })}
              rows={3}
              placeholder="Description (optional)"
              className="w-full min-h-[75px] max-h-[240px] px-2 py-1.5 rounded border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-sm text-[color:var(--color-ink-muted)] outline-none focus:border-[color:var(--color-accent)] focus:text-[color:var(--color-ink)] resize-y leading-relaxed"
            />
          </div>
        </div>
      </td>
      <td className="px-2 py-2 border-b border-[color:var(--color-rule)] min-w-[180px]">
        {/* Manual free-text entry — the reporter types the Project (and
         *  Module). A task-linked row keeps its auto-filled Space name as
         *  the starting value, still editable. */}
        <input
          value={item.project ?? ''}
          onChange={(e) => onPatch({ project: e.target.value })}
          placeholder="Project"
          className={editableClass}
        />
      </td>
      <td className="px-2 py-2 border-b border-[color:var(--color-rule)] min-w-[180px]">
        <input
          value={item.module ?? ''}
          onChange={(e) => onPatch({ module: e.target.value })}
          placeholder="Module"
          className={editableClass}
        />
      </td>
      <td className="px-2 py-2 border-b border-[color:var(--color-rule)] min-w-[200px]">
        <select
          value={item.type}
          onChange={(e) =>
            onPatch({ type: e.target.value })
          }
          className={editableClass}
        >
          {/* Explicit empty option — new rows start with type '' and
           *  a controlled <select> whose value matches NO option
           *  silently DISPLAYS the first catalogue entry while the
           *  state stays ''. The author read that ghost value (e.g.
           *  "Retesting") as selected, submitted, and the stored ''
           *  rendered as "—" on every viewer including the Company
           *  Admin's Work Reports page (QA-reported). With this row
           *  the display always equals the stored value; picking a
           *  real type fires onChange and persists it. '' remains
           *  valid on the wire (type is optional per the DTO). */}
          <option value="">Select type…</option>
          {taskTypeOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </td>
      <td className="px-2 py-2 border-b border-[color:var(--color-rule)] min-w-[170px]">
        <select
          value={item.status}
          onChange={(e) => onPatch({ status: e.target.value })}
          className={editableClass}
        >
          {itemStatusOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </td>
      <td className="px-2 py-2 border-b border-[color:var(--color-rule)] min-w-[140px]">
        <select
          value={item.priority}
          onChange={(e) =>
            onPatch({ priority: e.target.value })
          }
          className={editableClass}
        >
          {PRIORITY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </td>
      {/* Time — entered as hours + minutes, STORED as decimal hours
       *  (`timeSpent`): the backend sums these into `content.hoursLogged`
       *  (12h/day cap — see MAX_DAILY_HOURS). Combined h+m is clamped to the
       *  cap, so e.g. 12h forces minutes to 0. */}
      <td className="px-2 py-2 border-b border-[color:var(--color-rule)] min-w-[180px]">
        <div className="flex items-center gap-1">
          <input
            type="number"
            min={0}
            max={MAX_DAILY_HOURS}
            step={1}
            value={timeH}
            // Select on focus so the first keystroke replaces the default 0.
            onFocus={(e) => e.target.select()}
            onChange={(e) => {
              const raw = e.target.value
              const nh = raw === '' ? 0 : Math.max(0, Math.min(MAX_DAILY_HOURS, Math.floor(Number(raw) || 0)))
              onPatch({ timeSpent: hmToHours(nh, timeM) })
            }}
            placeholder="0"
            aria-label={`Hours spent on task ${index + 1}`}
            title="Hours spent on this task"
            className={hmInputClass}
          />
          <span aria-hidden="true" className="text-xs text-[color:var(--color-ink-muted)] shrink-0">h</span>
          <input
            type="number"
            min={0}
            max={59}
            step={1}
            value={timeM}
            onFocus={(e) => e.target.select()}
            onChange={(e) => {
              const raw = e.target.value
              const nm = raw === '' ? 0 : Math.max(0, Math.min(59, Math.floor(Number(raw) || 0)))
              onPatch({ timeSpent: hmToHours(timeH, nm) })
            }}
            placeholder="0"
            aria-label={`Minutes spent on task ${index + 1}`}
            title="Minutes spent on this task"
            className={hmInputClass}
          />
          <span aria-hidden="true" className="text-xs text-[color:var(--color-ink-muted)] shrink-0">m</span>
        </div>
      </td>
      <td className="px-2 py-2 border-b border-[color:var(--color-rule)]">
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove row"
          title="Remove row"
          className="p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
        >
          <Trash2 size={14} />
        </button>
      </td>
    </tr>
  )
}

// ---------- §3 Blockers ----------

function BlockersSection({
  items,
  onPatch,
  onRemove,
  onAdd,
  // Empty-state copy is period-aware: "today" on a daily report,
  // "this week" / "this month" on the weekly / monthly forms that reuse
  // this same component. Defaults to the daily wording.
  emptyLabel = 'No blockers logged today.',
}) {
  return (
    <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4 mb-4">
      <div className="flex items-baseline justify-between mb-1">
        <h3 className="text-sm uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] m-0">
          Issues & Dependencies
        </h3>
        {/* Count only blockers with text — a blank row added via
         *  "Add blocker" is dropped on submit, so it must not be tallied. */}
        <p className="text-sm text-[color:var(--color-ink-muted)]">
          {items.filter((b) => (b.blocker ?? '').trim().length > 0).length}
        </p>
      </div>
      <p className="text-sm text-[color:var(--color-ink-muted)] mb-3">
        Anything blocking you or someone you're waiting on.
      </p>

      {items.length === 0 ? (
        <p className="text-sm text-[color:var(--color-ink-muted)] italic mb-3">
          {emptyLabel}
        </p>
      ) : (
        <div className="space-y-2">
          {/* Column headers — match the Work Details table so users see
           *  the same scannable layout across every section of the report. */}
          <div className="hidden sm:grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_140px_32px] gap-2 items-center px-2 text-xs font-semibold text-[color:var(--color-ink-muted)]">
            <span>Blocker</span>
            <span>Dependency</span>
            <span>Status</span>
            <span aria-hidden="true" />
          </div>
          {items.map((b, i) => (
            <div
              key={i}
              className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_140px_32px] gap-2 items-start"
            >
              {/* Red field when the row has a dependency/status but no
               *  blocker text — such a row blocks submission instead of
               *  being silently dropped (QA). */}
              <input
                value={b.blocker}
                onChange={(e) => onPatch(i, { blocker: e.target.value })}
                placeholder="Blocker / challenge"
                aria-invalid={blockerRowMissingText(b)}
                className={
                  'h-9 px-2 rounded-md border bg-[color:var(--color-canvas)] text-sm text-[color:var(--color-ink)] outline-none ' +
                  (blockerRowMissingText(b)
                    ? 'border-[color:var(--color-priority-urgent)] focus:border-[color:var(--color-priority-urgent)]'
                    : 'border-[color:var(--color-rule)] focus:border-[color:var(--color-accent)]')
                }
              />
              <input
                value={b.dependencyOn ?? ''}
                onChange={(e) => onPatch(i, { dependencyOn: e.target.value })}
                placeholder="Dependency on (person / team / system)"
                className="h-9 px-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-sm text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)]"
              />
              <select
                value={b.resolutionStatus}
                onChange={(e) =>
                  onPatch(i, {
                    resolutionStatus: e.target.value,
                  })
                }
                className="h-9 px-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-sm text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)]"
              >
                {RESOLUTION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => onRemove(i)}
                aria-label="Remove blocker"
                title="Remove blocker"
                className="p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] hover:bg-[color:var(--color-card-hover)] cursor-pointer h-9 w-9 inline-flex items-center justify-center"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-3">
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border border-[color:var(--color-rule)] text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
        >
          <Plus size={13} /> Add Blocker
        </button>
      </div>
    </div>
  )
}

// ---------- §5 Meetings ----------

function MeetingsSection({
  items,
  onPatch,
  onRemove,
  onAdd,
}) {
  return (
    <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4 mb-4">
      <div className="flex items-baseline justify-between mb-1">
        <p className="text-sm uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]">
          Communication & collaboration
        </p>
        <p className="text-sm text-[color:var(--color-ink-muted)]">
          {/* Count only meetings with a title — blank rows are dropped on
           *  submit, so they shouldn't be tallied. */}
          {(() => {
            const titled = items.filter(
              (m) => (m.title ?? '').trim().length > 0,
            )
            const n = titled.length
            const mins = titled.reduce(
              (s, m) => s + Number(m.durationMinutes ?? 0),
              0,
            )
            const label = `${n} meeting${n === 1 ? '' : 's'}`
            return mins > 0 ? `${label} · ${formatHM(mins / 60)}` : label
          })()}
        </p>
      </div>
      <p className="text-sm text-[color:var(--color-ink-muted)] mb-3">
        Meetings you attended today.
      </p>

      {items.length === 0 ? (
        <p className="text-sm text-[color:var(--color-ink-muted)] italic mb-3">
          No meetings logged.
        </p>
      ) : (
        <div className="space-y-2">
          {/* Column headers — same pattern as the Work Details table so
           *  every section of the daily report reads consistently. */}
          <div className="hidden sm:grid grid-cols-[minmax(0,2fr)_150px_minmax(220px,1.3fr)_32px] gap-2 items-center px-2 text-xs font-semibold text-[color:var(--color-ink-muted)]">
            <span>Meeting</span>
            <span>Duration</span>
            <span>Attendees</span>
            <span aria-hidden="true" />
          </div>
          {items.map((m, i) => (
            <div
              key={i}
              className="grid grid-cols-1 sm:grid-cols-[minmax(0,2fr)_150px_minmax(220px,1.3fr)_32px] gap-2 items-start"
            >
              {/* Meeting + purpose combined in one field (stored as `title`;
               *  the optional `purpose` key is left for legacy reports). */}
              <input
                value={m.title}
                onChange={(e) => onPatch(i, { title: e.target.value })}
                placeholder="Meeting / purpose"
                className="h-9 px-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-sm text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)]"
              />
              {/* Duration as hours + minutes; stored as durationMinutes. */}
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min={0}
                  max={24}
                  step={1}
                  value={minutesToHM(m.durationMinutes).h}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => {
                    const nh = e.target.value === '' ? 0 : Math.max(0, Math.min(24, Math.floor(Number(e.target.value) || 0)))
                    onPatch(i, { durationMinutes: hmToMinutes(nh, minutesToHM(m.durationMinutes).m) })
                  }}
                  aria-label="Meeting duration — hours"
                  className="h-9 w-14 px-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-sm text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                />
                <span aria-hidden="true" className="text-xs text-[color:var(--color-ink-muted)] shrink-0">h</span>
                <input
                  type="number"
                  min={0}
                  max={59}
                  step={1}
                  value={minutesToHM(m.durationMinutes).m}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => {
                    const nm = e.target.value === '' ? 0 : Math.max(0, Math.min(59, Math.floor(Number(e.target.value) || 0)))
                    onPatch(i, { durationMinutes: hmToMinutes(minutesToHM(m.durationMinutes).h, nm) })
                  }}
                  aria-label="Meeting duration — minutes"
                  className="h-9 w-14 px-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-sm text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                />
                <span aria-hidden="true" className="text-xs text-[color:var(--color-ink-muted)] shrink-0">m</span>
              </div>
              <input
                value={m.attendees ?? ''}
                onChange={(e) => onPatch(i, { attendees: e.target.value })}
                placeholder="Attendees"
                className="h-9 px-2 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-sm text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)]"
              />
              <button
                type="button"
                onClick={() => onRemove(i)}
                aria-label="Remove meeting"
                title="Remove meeting"
                className="p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] hover:bg-[color:var(--color-card-hover)] cursor-pointer h-9 w-9 inline-flex items-center justify-center"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-3">
        <button
          type="button"
          onClick={onAdd}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border border-[color:var(--color-rule)] text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
        >
          <Plus size={13} /> Add Meeting
        </button>
      </div>
    </div>
  )
}
